import './index.css';
import { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Link, useParams, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useForm } from 'react-hook-form';
import { MegaphoneIcon, InformationCircleIcon, ChartPieIcon, ChatBubbleLeftRightIcon, ShieldCheckIcon, PuzzlePieceIcon, BanknotesIcon, XMarkIcon, Bars3Icon, UserCircleIcon, ArrowRightOnRectangleIcon, HomeIcon, MapPinIcon, CalendarDaysIcon, BuildingOffice2Icon, TagIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { contentApi } from './api/content';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import DGIIFormsManager from './components/dgii/DGIIFormsManager';
import AdminDGIIView from './components/dgii/AdminDGIIView';
import GestionEmpresas from './components/empresas/GestionEmpresas';
import EmpresasCarpetaAdmin from './components/empresas/EmpresasCarpetaAdmin';
import GestionEmpresasAdmin from './components/empresas/GestionEmpresasAdmin';
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import ProtectedRoute from './components/auth/ProtectedRoute';
import UserManagement from './components/auth/UserManagement';
import AdminLayout from './components/admin/AdminLayout';
import GestionActividadesEconomicas from './components/admin/GestionActividadesEconomicas';
import AdminDashboard from './components/admin/AdminDashboard';
import Settings from './components/admin/Settings';
import SystemSettings from './components/admin/SystemSettings';
import HeroManagement from './components/admin/HeroManagement';
import TestimonialManagement from './components/admin/TestimonialManagement';
import PartnerManagement from './components/admin/PartnerManagement';
import ProjectStatisticManagement from './components/admin/ProjectStatisticManagement';
import BlogManagement from './components/admin/BlogManagement';
import AuditLog from './components/admin/AuditLog';
import IR17Manager from './components/dgii/IR17Manager';
import EmpresasEnCarpeta from './components/landing/EmpresasEnCarpeta';
import VenderEmpresa from './components/landing/VenderEmpresa';
import GestionDocumental from './components/documental/GestionDocumental';
import FormsFillManager from './components/formsFill/FormsFillManager';
import ClienteCambiosDocumental from './components/documental/ClienteCambiosDocumental';

import { API_URL } from './config/api';

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			retry: 1, // Solo reintentar 1 vez en caso de error
			retryDelay: 1000, // Esperar 1 segundo entre reintentos
			refetchOnWindowFocus: false, // No refetch al cambiar de ventana
			refetchOnMount: true, // Refetch al montar el componente
			refetchOnReconnect: true, // Refetch al reconectar
			staleTime: 5 * 60 * 1000, // 5 minutos
			cacheTime: 10 * 60 * 1000, // 10 minutos
		},
	},
});
const CONTACT_EMAIL = process.env.REACT_APP_CONTACT_EMAIL || 'info@psasesoresconsultores.com';
const WHATSAPP_NUMBER = process.env.REACT_APP_WHATSAPP || '18496079129';
const SOCIAL_FACEBOOK = process.env.REACT_APP_FACEBOOK_URL || 'https://www.facebook.com/psasesoresconsultores';
const SOCIAL_INSTAGRAM = process.env.REACT_APP_INSTAGRAM_URL || 'https://instagram.com/psasesoresconsultores';
const SOCIAL_LINKEDIN = process.env.REACT_APP_LINKEDIN_URL || 'https://www.linkedin.com/in/paulino-sosa-constituci%C3%B3n-de-empresas-contabilidad-impuestos-000174377/';
const SOCIAL_YOUTUBE = process.env.REACT_APP_YOUTUBE_URL || '#';
const SOCIAL_TWITTER = process.env.REACT_APP_TWITTER_URL || '#';
const SOCIAL_TIKTOK = process.env.REACT_APP_TIKTOK_URL || 'https://www.tiktok.com/@psasesoresconsultores';
const SOCIAL_THREADS = process.env.REACT_APP_THREADS_URL || 'https://threads.net/@psasesoresconsultores';

function FacebookIcon(props){return (<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}><path d="M22 12a10 10 0 1 0-11.6 9.9v-7H8v-3h2.4V9.5A3.4 3.4 0 0 1 13.9 6h2.7v3h-2c-.9 0-1.1.4-1.1 1.1V12H17l-.5 3h-2.9v7A10 10 0 0 0 22 12z"/></svg>);} 
function InstagramIcon(props){return (<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}><path d="M7 2h10a5 5 0 0 1 5 5v10a5 5 0 0 1-5 5H7a5 5 0 0 1-5-5V7a5 5 0 0 1 5-5zm0 2a3 3 0 0 0-3 3v10a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3V7a3 3 0 0 0-3-3H7zm5 3.5A4.5 4.5 0 1 1 7.5 12 4.5 4.5 0 0 1 12 7.5zm0 2A2.5 2.5 0 1 0 14.5 12 2.5 2.5 0 0 0 12 9.5zm5.25-3.25a1 1 0 1 1-1 1 1 1 0 0 1 1-1z"/></svg>);} 
function LinkedinIcon(props){return (<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}><path d="M4.98 3.5A2.5 2.5 0 1 1 2.5 6 2.5 2.5 0 0 1 4.98 3.5zM3 8h4v13H3zM9 8h3.8v1.8h.1A4.2 4.2 0 0 1 17 7.8c3 0 3.6 2 3.6 4.6V21h-4v-6.2c0-1.5 0-3.5-2.1-3.5s-2.4 1.6-2.4 3.4V21H9z"/></svg>);} 
function YoutubeIcon(props){return (<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}><path d="M23.5 7.1a3 3 0 0 0-2.1-2.1C19.2 4.5 12 4.5 12 4.5s-7.2 0-9.4.5A3 3 0 0 0 .5 7.1 31.3 31.3 0 0 0 0 12a31.3 31.3 0 0 0 .5 4.9 3 3 0 0 0 2.1 2.1c2.2.5 9.4.5 9.4.5s7.2 0 9.4-.5a3 3 0 0 0 2.1-2.1A31.3 31.3 0 0 0 24 12a31.3 31.3 0 0 0-.5-4.9zM9.8 15.1v-6l6 3-6 3z"/></svg>);} 
function XIcon(props){return (<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}><path d="M3 3h3.2l6.2 7.6L17.8 3H21l-7.4 9.2L21 21h-3.2l-6.7-8.2L6.2 21H3l7.7-9.6L3 3z"/></svg>);} 
function TikTokIcon(props){return (<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}><path d="M14.5 3c1.1 1.7 2.8 2.8 4.8 3V9c-1.8-.1-3.4-.7-4.8-1.7V14a6 6 0 1 1-6-6h1.5v3H9a3 3 0 1 0 3 3V3h2.5z"/></svg>);}
function ThreadsIcon(props){return (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}><path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2z"/><path d="M12 6.5c-3.038 0-5.5 2.462-5.5 5.5s2.462 5.5 5.5 5.5c1.528 0 2.93-.624 3.95-1.64l-1.415-1.414C13.767 16.36 12.922 17 12 17c-2.757 0-5-2.243-5-5s2.243-5 5-5c.922 0 1.767.64 2.035 1.534l1.415-1.414C14.93 7.124 13.528 6.5 12 6.5z"/></svg>);} 

// Array de servicios disponible globalmente
const serviciosList = [
	{
		title: 'Constitución de Empresas & ONG',
		desc: 'Acompañamiento integral desde el asesoramiento inicial hasta la inscripción final para que inicies con confianza.',
		linkKey: 'form_url_constitucion'
	},
	{
		title: 'Outsourcing contable y fiscal',
		desc: 'Planes de igualas contables adaptados a tu empresa: desde básico hasta solución integral completa.',
		details: 'Ofrecemos planes de igualas contables diseñados para adaptarse a las necesidades de tu empresa. Desde el plan básico para declaraciones esenciales hasta el plan plus con servicios integrales, te acompañamos en toda tu gestión contable y fiscal.',
		features: [
			'Planes flexibles según tus necesidades',
			'Gestión completa de declaraciones fiscales',
			'Asesoría financiera y fiscal',
			'Elaboración de nómina',
			'Estados financieros e informes'
		]
	},
	{
		title: 'Gestiones legales empresariales',
		desc: 'Trámites y obligaciones legales para mantener tu empresa al día con la regulación vigente.',
		details: 'Nuestro equipo de expertos se encarga de todas las gestiones legales necesarias para establecer y mantener tu empresa en cumplimiento con las regulaciones vigentes. Desde la constitución inicial hasta el cumplimiento continuo de obligaciones legales, estamos aquí para ayudarte en cada paso del camino.',
		features: [
			'Asesoramiento para estructura empresarial',
			'Redacción de documentos legales',
			'Gestión de contratos',
			'Cumplimiento de obligaciones legales',
			'Seguimiento y renovaciones'
		],
		linkKey: 'form_url_gestiones_legales'
	},
	{
		title: 'Renovación del Registro Mercantil',
		desc: 'Proceso ágil y seguro para que tu operación no se detenga por vencimientos.',
		details: 'Nuestro equipo de profesionales se encarga de todos los trámites necesarios para renovar el registro mercantil de tu empresa de manera rápida y eficiente. Nos aseguramos de cumplir con todos los requisitos legales y administrativos para garantizar que tu negocio continúe operando sin interrupciones.',
		features: [
			'Renovación rápida y eficiente',
			'Actualización de información empresarial',
			'Presentación de informes financieros',
			'Cumplimiento de requisitos legales',
			'Seguimiento del proceso'
		],
		linkKey: 'form_url_renovacion_mercantil'
	},
	{
		title: 'Asesoría fiscal',
		desc: 'Planificación, presentación y resolución de incidencias fiscales con enfoque en la toma de decisiones.',
		details: 'Nuestro equipo de expertos en asesoría fiscal está aquí para proporcionarte orientación personalizada en materia de impuestos, ayudándote a tomar decisiones informadas que beneficien a tu empresa. Desde la planificación fiscal hasta la presentación de declaraciones y la resolución de problemas fiscales, estamos aquí para ayudarte en cada paso del camino.',
		features: [
			'Planificación fiscal estratégica',
			'Presentación de declaraciones',
			'Resolución de problemas fiscales',
			'Orientación personalizada',
			'Optimización tributaria'
		],
		linkKey: 'form_url_asesoria_fiscal'
	},
	{
		title: 'Estados Financieros',
		desc: 'Elaboración oportuna de estados y análisis para entender tu situación financiera y decidir mejor.',
		details: 'Nuestro equipo de expertos en contabilidad se encarga de la preparación de estados financieros precisos y oportunos, incluyendo el balance general, el estado de resultados y el estado de flujo de efectivo. Además, te proporcionamos un análisis detallado de estos estados financieros para ayudarte a entender mejor la situación financiera de tu empresa y tomar decisiones informadas.',
		features: [
			'Balance general',
			'Estado de resultados',
			'Estado de flujo de efectivo',
			'Análisis financiero detallado',
			'Reportes oportunos'
		],
		linkKey: 'form_url_estados_financieros'
	},
	{
		title: 'Auditorías',
		desc: 'Revisión minuciosa de registros, controles y procesos para detectar riesgos y oportunidades.',
		details: 'Nuestro equipo de auditores altamente capacitados se encarga de revisar minuciosamente tus registros financieros, procesos internos y controles, identificando posibles riesgos, oportunidades de mejora y áreas de cumplimiento. Nuestra experiencia en una amplia gama de sectores nos permite ofrecerte una auditoría exhaustiva y precisa, adaptada a las necesidades específicas de tu empresa.',
		features: [
			'Revisión de registros financieros',
			'Evaluación de procesos internos',
			'Identificación de riesgos',
			'Oportunidades de mejora',
			'Informe detallado'
		],
		linkKey: 'form_url_auditorias'
	},
	{
		title: 'Inventarios',
		desc: 'Levantamiento preciso y recomendaciones para eficiencia, ahorro y disminución de obsolescencia.',
		details: 'Nuestro equipo de expertos se encarga de realizar un inventario detallado y preciso de tus productos, utilizando herramientas y tecnologías avanzadas para garantizar la exactitud de los datos. Además, te proporcionamos análisis y recomendaciones para mejorar la eficiencia en la gestión de inventarios, reducir costos de almacenamiento y minimizar el riesgo de obsolescencia.',
		features: [
			'Inventario detallado y preciso',
			'Tecnologías avanzadas',
			'Análisis de eficiencia',
			'Reducción de costos',
			'Minimización de obsolescencia'
		],
		linkKey: 'form_url_inventarios'
	},
	{
		title: 'Imagen Corporativa',
		desc: 'Desarrollo de identidad visual coherente con tus objetivos y puntos de contacto.',
		details: 'Nuestro equipo de diseñadores expertos trabaja contigo para desarrollar una imagen corporativa única y memorable, que abarca desde el diseño de logotipos y colores corporativos hasta la creación de materiales de marketing y comunicación visual. Además, te proporcionamos orientación estratégica para asegurarnos de que tu imagen corporativa esté alineada con tus valores y objetivos empresariales, y sea coherente en todos los puntos de contacto con tus clientes y stakeholders.',
		features: [
			'Diseño de logotipos',
			'Colores corporativos',
			'Materiales de marketing',
			'Comunicación visual',
			'Orientación estratégica'
		],
		linkKey: 'form_url_imagen_corporativa'
	},
	{
		title: 'Diseño de Logo Empresarial',
		desc: 'Proceso colaborativo con propuestas y revisiones para un resultado que represente tu marca.',
		details: 'Nuestro equipo de diseñadores talentosos trabaja estrechamente contigo para comprender tu visión, valores y estilo de marca. A partir de ahí, creamos varios conceptos de logo que capturan la esencia de tu empresa y resuenan con tu audiencia objetivo. Además, te brindamos tres revisiones para garantizar que estés completamente satisfecho con el diseño final.',
		features: [
			'Varios conceptos de logo',
			'Tres revisiones incluidas',
			'Comprensión de tu visión',
			'Diseño desde cero o refrescamiento',
			'Satisfacción garantizada'
		],
		linkKey: 'form_url_diseno_logo'
	},
	{
		title: 'Elaboración de Sellos',
		desc: 'Sellos personalizados con materiales duraderos y alta calidad.',
		details: 'Nuestro equipo de diseño se encarga de crear sellos que reflejen la identidad de tu marca y cumplan con tus especificaciones exactas. Ya sea que necesites un sello para uso comercial, legal o personal, estamos aquí para ayudarte a crear un diseño único y de alta calidad. Utilizamos tecnología de vanguardia y materiales duraderos para garantizar que tus sellos sean de la más alta calidad y puedan resistir el uso diario.',
		features: [
			'Diseño personalizado',
			'Tecnología de vanguardia',
			'Materiales duraderos',
			'Uso comercial, legal o personal',
			'Alta calidad garantizada'
		],
		linkKey: 'form_url_elaboracion_sellos'
	},
	{
		title: 'Asesoría Financiera',
		desc: 'Orientación estratégica para optimizar recursos, planificar crecimiento y tomar decisiones financieras informadas.',
		details: 'Nuestro equipo de expertos en asesoría financiera te ayuda a desarrollar estrategias que optimicen el uso de tus recursos, mejoren la rentabilidad y faciliten el crecimiento sostenible de tu empresa. Proporcionamos análisis detallados, proyecciones financieras y recomendaciones personalizadas para fortalecer la salud financiera de tu negocio.',
		features: [
			'Análisis financiero detallado',
			'Planificación estratégica',
			'Optimización de recursos',
			'Proyecciones financieras',
			'Recomendaciones personalizadas'
		],
		linkKey: 'form_url_asesoria_financiera'
	},
	{
		title: 'Manejo de Redes Sociales',
		desc: 'Estrategia, contenido y campañas para maximizar alcance, participación y conversión.',
		details: 'Nuestro equipo de expertos en redes sociales se encarga de crear y ejecutar estrategias personalizadas que maximicen el alcance, la participación y la conversión en tus plataformas sociales. Desde la creación de contenido atractivo hasta la gestión de campañas publicitarias, estamos aquí para ayudarte a alcanzar tus objetivos de marketing en redes sociales.',
		features: [
			'Estrategias personalizadas',
			'Creación de contenido atractivo',
			'Gestión de campañas publicitarias',
			'Maximización de alcance',
			'Análisis y métricas'
		],
		linkKey: 'form_url_redes_sociales'
	},
	{
		title: 'Pautas Publicitarias en TV',
		desc: 'Producción y gestión de pautas publicitarias en televisión para maximizar el impacto de tu marca.',
		details: 'Nuestro equipo se encarga de la producción, gestión y colocación de pautas publicitarias en televisión, ayudándote a llegar a tu audiencia objetivo de manera efectiva. Desde la conceptualización del mensaje hasta la negociación de espacios y horarios estratégicos, gestionamos todo el proceso para maximizar el retorno de inversión de tu campaña publicitaria.',
		features: [
			'Producción de comerciales',
			'Negociación de espacios y horarios',
			'Estrategia de segmentación',
			'Seguimiento y análisis de resultados',
			'Maximización de ROI'
		],
		linkKey: 'form_url_pautas_tv'
	},
	{
		title: 'Posicionamiento de Marca',
		desc: 'Estrategias integrales para construir y fortalecer la presencia de tu marca en el mercado.',
		details: 'Desarrollamos estrategias integrales de posicionamiento de marca que te ayudan a diferenciarte de la competencia y construir una presencia sólida en el mercado. Nuestro enfoque incluye análisis de mercado, definición de propuesta de valor, estrategias de comunicación y gestión de reputación para fortalecer la percepción y el reconocimiento de tu marca.',
		features: [
			'Análisis de mercado y competencia',
			'Definición de propuesta de valor',
			'Estrategias de comunicación',
			'Gestión de reputación',
			'Medición de impacto y reconocimiento'
		],
		linkKey: 'form_url_posicionamiento_marca'
	},
	{
		title: 'Habilitación de Centros de Salud',
		desc: 'Asesoría y gestión completa para la habilitación legal y operativa de centros de salud.',
		details: 'Ofrecemos servicios integrales de asesoría y gestión para la habilitación de centros de salud, clínicas y establecimientos médicos. Nuestro equipo conoce a fondo los requisitos legales, normativos y administrativos necesarios para operar en el sector salud, garantizando el cumplimiento de todas las regulaciones vigentes.',
		features: [
			'Asesoría en requisitos legales y normativos',
			'Gestión de permisos y licencias',
			'Cumplimiento de regulaciones sanitarias',
			'Preparación de documentación técnica',
			'Seguimiento del proceso de habilitación'
		],
		linkKey: 'form_url_centros_salud'
	},
	{
		title: 'Empresas en Carpeta',
		desc: 'Servicio de consultoría especializada para empresas que requieren atención en carpeta.',
		details: 'Ofrecemos servicios de consultoría especializada para empresas que operan bajo el régimen de "carpeta", proporcionando asesoría personalizada en cumplimiento fiscal, contable y administrativo. Nuestro equipo está especializado en entender las particularidades de este régimen y ayudar a las empresas a mantenerse en cumplimiento mientras optimizan sus operaciones.',
		features: [
			'Asesoría especializada en régimen de carpeta',
			'Cumplimiento fiscal y contable',
			'Gestión de documentación',
			'Optimización de operaciones',
			'Seguimiento continuo'
		],
		linkKey: 'form_url_empresas_carpeta'  // Se cargará dinámicamente desde la API
	},
	{
		title: 'Registro de Nombre o Marca Comercial',
		desc: 'Protección legal de tu nombre comercial y marca para diferenciarte en el mercado.',
		details: 'Realizamos el registro de nombres comerciales y marcas ante las autoridades competentes, protegiendo tu identidad empresarial y garantizando que puedas operar con exclusividad en el mercado. Nuestro servicio incluye búsqueda de disponibilidad, preparación de documentación y seguimiento del proceso de registro.',
		features: [
			'Búsqueda de disponibilidad de nombre/marca',
			'Preparación de documentación legal',
			'Tramitación ante autoridades competentes',
			'Seguimiento del proceso de registro',
			'Asesoría en protección de marca'
		],
		linkKey: 'form_url_registro_marca'
	},
	{
		title: 'Facturación Electrónica',
		desc: 'Implementación y gestión de sistemas de facturación electrónica para cumplimiento normativo.',
		details: 'Implementamos y gestionamos sistemas de facturación electrónica que cumplen con todas las normativas vigentes. Nuestro servicio incluye la configuración del sistema, capacitación del personal, integración con sistemas existentes y soporte continuo para garantizar una transición fluida y el cumplimiento permanente de las obligaciones fiscales.',
		features: [
			'Implementación de sistema de facturación electrónica',
			'Configuración y personalización',
			'Capacitación del personal',
			'Integración con sistemas existentes',
			'Soporte técnico continuo'
		],
		linkKey: 'form_url_facturacion_electronica'
	}
];

function Navbar({ onServiceClick }) {
	const location = useLocation();
	const navigate = useNavigate();
	const { user, logout, isAdmin, isGestor } = useAuth();
	const isHomePage = location.pathname === '/';
	const [showServicesDropdown, setShowServicesDropdown] = useState(false);
	const [showMobileMenu, setShowMobileMenu] = useState(false);
	const [showUserMenu, setShowUserMenu] = useState(false);
	const dropdownTimeoutRef = useRef(null);
	const userMenuTimeoutRef = useRef(null);

	const handleUserMenuEnter = () => {
		if (userMenuTimeoutRef.current) {
			clearTimeout(userMenuTimeoutRef.current);
			userMenuTimeoutRef.current = null;
		}
		setShowUserMenu(true);
	};

	const handleUserMenuLeave = () => {
		userMenuTimeoutRef.current = setTimeout(() => {
			setShowUserMenu(false);
		}, 220);
	};
	
	const handleLogout = async () => {
		await logout();
		navigate('/');
		setShowUserMenu(false);
	};

	const handleNavClick = (sectionId) => {
		if (isHomePage) {
			// Si estamos en la página principal, hacer scroll suave al elemento
			const element = document.getElementById(sectionId);
			if (element) {
				element.scrollIntoView({ behavior: 'smooth' });
			}
		} else {
			// Si estamos en otra página, navegar a la principal con el hash
			window.location.href = `/#${sectionId}`;
		}
	};

	const handleLogoClick = (e) => {
		if (isHomePage) {
			// Si estamos en la página principal, hacer scroll al inicio
			e.preventDefault();
			window.scrollTo({ top: 0, behavior: 'smooth' });
		} else {
			// Si estamos en otra página, navegar a la principal
			// El Link se encargará de la navegación
		}
	};

	const handleServiceClick = (service) => {
		// Cancelar cualquier timeout pendiente
		if (dropdownTimeoutRef.current) {
			clearTimeout(dropdownTimeoutRef.current);
			dropdownTimeoutRef.current = null;
		}
		setShowServicesDropdown(false);
		
		// Si no estamos en la página principal, navegar primero
		if (!isHomePage) {
			// Guardar el servicio en sessionStorage para que App lo pueda usar
			sessionStorage.setItem('pendingService', JSON.stringify(service));
			navigate('/');
			// Esperar a que la página se cargue completamente
			setTimeout(() => {
				if (onServiceClick) {
					onServiceClick(service);
				}
			}, 500);
		} else {
			// Si estamos en la página principal, abrir el modal directamente
			if (onServiceClick) {
				onServiceClick(service);
			}
		}
	};

	const handleMouseEnterDropdown = () => {
		// Cancelar cualquier timeout pendiente
		if (dropdownTimeoutRef.current) {
			clearTimeout(dropdownTimeoutRef.current);
			dropdownTimeoutRef.current = null;
		}
		setShowServicesDropdown(true);
	};

	const handleMouseLeaveDropdown = () => {
		// Agregar un pequeño delay antes de cerrar para permitir movimiento suave
		dropdownTimeoutRef.current = setTimeout(() => {
			setShowServicesDropdown(false);
		}, 150);
	};

	// Cleanup del timeout cuando el componente se desmonte
	useEffect(() => {
		return () => {
			if (dropdownTimeoutRef.current) {
				clearTimeout(dropdownTimeoutRef.current);
			}
			if (userMenuTimeoutRef.current) {
				clearTimeout(userMenuTimeoutRef.current);
			}
		};
	}, []);

  return (
		<nav className="bg-white/95 backdrop-blur-md sticky top-0 z-50 border-b border-gray-100 shadow-sm">
			<div className="container-width flex items-center h-16 sm:h-18 relative gap-4 px-4">
				{/* Logo - Izquierda */}
				<Link to="/" className="flex items-center gap-0 flex-shrink-0 group" onClick={handleLogoClick}>
					<img src="/logo_header0.png" alt="PS" className="h-8 sm:h-9 md:h-11 transition-transform duration-200 group-hover:scale-105" />
					<img src="/logo_header.png" alt="PS Asesores & Consultores" className="h-8 sm:h-9 md:h-11 hidden sm:block transition-opacity duration-200 group-hover:opacity-90" />
				</Link>
				
				{/* Menú Desktop - Centrado */}
				<div className="hidden lg:flex gap-4 xl:gap-6 text-sm font-medium items-center justify-center flex-1 mx-4">
					<div 
						className="relative"
						onMouseEnter={handleMouseEnterDropdown}
						onMouseLeave={handleMouseLeaveDropdown}
					>
						<button 
							onClick={() => handleNavClick('servicios')} 
							className="text-gray-700 hover:text-primary text-left flex items-center gap-1.5 transition-all duration-200 py-2 px-1 relative group"
						>
							<span className="relative">
								Servicios
								<span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary transition-all duration-200 group-hover:w-full"></span>
							</span>
							<svg className="w-4 h-4 transition-transform duration-200" style={{ transform: showServicesDropdown ? 'rotate(180deg)' : 'rotate(0deg)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
							</svg>
						</button>
						{/* Dropdown de servicios - Full width con colores de marca */}
						{showServicesDropdown && (
							<>
								{/* Overlay para cerrar al hacer click fuera */}
								<div 
									className="fixed inset-0 bg-black/10 z-40"
									style={{ top: '64px', pointerEvents: 'none' }}
									onClick={() => setShowServicesDropdown(false)}
								></div>
								{/* Dropdown */}
								<div 
									className="services-dropdown fixed left-0 right-0 top-16 bg-primary text-white shadow-2xl z-50 dropdown-apple"
									style={{ marginTop: 0 }}
									onMouseEnter={handleMouseEnterDropdown}
									onMouseLeave={handleMouseLeaveDropdown}
								>
									<div className="container-width py-6 md:py-10 px-4 md:px-8">
										<div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8 md:gap-12 xl:gap-16">
											{/* Primera columna - Servicios Principales */}
											<div>
												<h3 className="text-secondary/90 text-xs md:text-sm font-semibold uppercase tracking-wider mb-4 md:mb-6">
													Servicios Principales
												</h3>
												<ul className="space-y-3">
													{serviciosList.slice(0, 7).map((service, index) => (
														<li key={index}>
															<button
																onClick={() => handleServiceClick(service)}
																className="text-white font-medium text-sm hover:text-secondary transition-all duration-200 text-left w-full py-1.5 hover:translate-x-1"
															>
																{service.title}
															</button>
														</li>
													))}
												</ul>
											</div>
											
											{/* Segunda columna - Servicios Adicionales */}
											<div>
												<h3 className="text-secondary/90 text-xs md:text-sm font-semibold uppercase tracking-wider mb-4 md:mb-6">
													Servicios Adicionales
												</h3>
												<ul className="space-y-3">
													{serviciosList.slice(7, 14).map((service, index) => (
														<li key={index + 7}>
															<button
																onClick={() => handleServiceClick(service)}
																className="text-white font-medium text-sm hover:text-secondary transition-all duration-200 text-left w-full py-1.5 hover:translate-x-1"
															>
																{service.title}
															</button>
														</li>
													))}
												</ul>
											</div>
											
											{/* Tercera columna - Más Servicios */}
											<div>
												<h3 className="text-secondary/90 text-xs md:text-sm font-semibold uppercase tracking-wider mb-4 md:mb-6">
													Más Servicios
												</h3>
												<ul className="space-y-3">
													{serviciosList.slice(14).map((service, index) => (
														<li key={index + 14}>
															<button
																onClick={() => handleServiceClick(service)}
																className="text-white font-medium text-sm hover:text-secondary transition-all duration-200 text-left w-full py-1.5 hover:translate-x-1"
															>
																{service.title}
															</button>
														</li>
													))}
													<li className="mt-8 pt-6 border-t border-white/20">
														<button
															onClick={() => {
																handleNavClick('servicios');
																setShowServicesDropdown(false);
															}}
															className="text-white font-semibold text-sm hover:text-secondary transition-all duration-200 text-left w-full flex items-center gap-2 group"
														>
															Ver todos los servicios
															<svg className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
															</svg>
														</button>
													</li>
												</ul>
											</div>
										</div>
									</div>
								</div>
							</>
						)}
					</div>
					<button onClick={() => handleNavClick('casos')} className="text-gray-700 hover:text-primary text-left py-2 px-1 relative group transition-all duration-200">
						<span className="relative">
							Casos
							<span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary transition-all duration-200 group-hover:w-full"></span>
						</span>
					</button>
					<button onClick={() => handleNavClick('nosotros')} className="text-gray-700 hover:text-primary text-left py-2 px-1 relative group transition-all duration-200">
						<span className="relative">
							Nosotros
							<span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary transition-all duration-200 group-hover:w-full"></span>
						</span>
					</button>
					<Link to="/blog" className="text-gray-700 hover:text-primary py-2 px-1 relative group transition-all duration-200">
						<span className="relative">
							Blog
							<span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary transition-all duration-200 group-hover:w-full"></span>
						</span>
					</Link>
					<Link to="/referidores" className="text-gray-700 hover:text-primary py-2 px-1 relative group transition-all duration-200">
						<span className="relative">
							Referidores
							<span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary transition-all duration-200 group-hover:w-full"></span>
						</span>
					</Link>
					<Link to="/empresas-en-carpeta" className="text-gray-700 hover:text-primary py-2 px-1 relative group transition-all duration-200">
						<span className="relative">
							Empresas en Carpeta
							<span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary transition-all duration-200 group-hover:w-full"></span>
						</span>
					</Link>
					<Link to="/vender-empresa" className="text-secondary hover:text-primary py-2 px-1 relative group transition-all duration-200 font-semibold">
						<span className="relative">
							Vender mi empresa
							<span className="absolute bottom-0 left-0 w-0 h-0.5 bg-secondary transition-all duration-200 group-hover:w-full"></span>
						</span>
					</Link>
					<button onClick={() => handleNavClick('contacto')} className="text-gray-700 hover:text-primary text-left py-2 px-1 relative group transition-all duration-200">
						<span className="relative">
							Contacto
							<span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary transition-all duration-200 group-hover:w-full"></span>
						</span>
					</button>
				</div>
				
				{/* Botones de acción Desktop - Derecha */}
				<div className="hidden lg:flex gap-2 items-center justify-end flex-shrink-0 ml-auto">
					{user ? (
						<>
							{/* Menú de usuario: retardo al salir + puente sin hueco para no cerrar al mover el cursor */}
							<div
								className="relative"
								onMouseEnter={handleUserMenuEnter}
								onMouseLeave={handleUserMenuLeave}
							>
								<button
									type="button"
									onClick={() => setShowUserMenu((prev) => !prev)}
									className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-xs font-medium whitespace-nowrap transition-all duration-200 shadow-sm hover:shadow-md touch-manipulation"
								>
									<UserCircleIcon className="h-4 w-4" />
									<span className="hidden lg:inline">
										{user.first_name && user.last_name 
											? `${user.first_name} ${user.last_name}`.split(' ').slice(0, 2).join(' ')
											: user.username}
									</span>
									<svg 
										className={`h-4 w-4 transition-transform duration-200 ${showUserMenu ? 'rotate-180' : ''}`} 
										fill="none" 
										stroke="currentColor" 
										viewBox="0 0 24 24"
									>
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
									</svg>
								</button>
								{/* Dropdown del menú de usuario (top-full + pt = zona de hover continua) */}
								{showUserMenu && (
									<div className="absolute right-0 top-full z-50 pt-1.5 w-64">
									<div className="bg-white rounded-lg shadow-xl border border-gray-200 py-2">
										{/* Información del usuario */}
										<div className="px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-primary/5 to-secondary/5">
											<div className="flex items-center gap-3">
												<div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
													<UserCircleIcon className="h-6 w-6 text-primary" />
												</div>
												<div className="flex-1 min-w-0">
													<p className="text-sm font-semibold text-gray-900 truncate">
														{user.first_name && user.last_name 
															? `${user.first_name} ${user.last_name}`
															: user.username}
													</p>
													{user.email && (
														<p className="text-xs text-gray-500 truncate">{user.email}</p>
													)}
													{user.profile && (
														<span className={`inline-block mt-1 px-2 py-0.5 text-xs font-medium rounded-full ${
															user.profile.role === 'admin' ? 'bg-red-100 text-red-800' :
															user.profile.role === 'gestor' ? 'bg-blue-100 text-blue-800' :
															'bg-green-100 text-green-800'
														}`}>
															{user.profile.role_display || user.profile.role}
														</span>
													)}
												</div>
											</div>
										</div>
										
										{/* Opciones del menú */}
										<div className="py-1">
											<Link
												to="/"
												onClick={() => setShowUserMenu(false)}
												className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
											>
												<HomeIcon className="h-5 w-5 text-blue-600" />
												<span>Página Principal</span>
											</Link>
											<Link
												to="/admin/dashboard"
												onClick={() => setShowUserMenu(false)}
												className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
											>
												<ShieldCheckIcon className="h-5 w-5 text-primary" />
												<span>Inicio administrativo</span>
											</Link>
											<Link
												to="/dgii"
												onClick={() => setShowUserMenu(false)}
												className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
											>
												<BanknotesIcon className="h-5 w-5 text-green-600" />
												<span>Formularios DGII</span>
											</Link>
											{isGestor() && (
												<Link
													to="/dgii/admin"
													onClick={() => setShowUserMenu(false)}
													className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
												>
													<ChartPieIcon className="h-5 w-5 text-blue-600" />
													<span>Admin DGII</span>
												</Link>
											)}
											{isAdmin() && (
												<Link
													to="/admin/users"
													onClick={() => setShowUserMenu(false)}
													className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
												>
													<ShieldCheckIcon className="h-5 w-5 text-purple-600" />
													<span>Gestión de Usuarios</span>
												</Link>
											)}
										</div>
										
										{/* Separador */}
										<div className="border-t border-gray-200 my-1"></div>
										
										{/* Cerrar sesión */}
										<button
											type="button"
											onClick={handleLogout}
											className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
										>
											<ArrowRightOnRectangleIcon className="h-5 w-5" />
											<span>Cerrar Sesión</span>
										</button>
									</div>
									</div>
								)}
							</div>
						</>
						) : (
							<Link to="/login" className="px-3 py-1.5 bg-primary text-white rounded-lg text-xs font-medium whitespace-nowrap hover:opacity-90 active:opacity-80 transition-all duration-200 shadow-sm hover:shadow-md touch-manipulation">
								Iniciar Sesión
							</Link>
						)}
				</div>
				
				{/* Botón menú móvil - Derecha */}
				<button
					onClick={() => setShowMobileMenu(!showMobileMenu)}
					className="lg:hidden p-2.5 text-gray-700 hover:text-primary active:text-primary transition-all duration-200 touch-manipulation ml-auto rounded-lg hover:bg-gray-100"
					aria-label="Toggle menu"
				>
					{showMobileMenu ? (
						<XMarkIcon className="h-6 w-6 transition-transform duration-200" />
					) : (
						<Bars3Icon className="h-6 w-6 transition-transform duration-200" />
					)}
				</button>
			</div>
			
			{/* Menú móvil */}
			{showMobileMenu && (
				<div className="lg:hidden border-t border-gray-100 bg-white shadow-lg">
					<div className="container-width py-4 space-y-2 px-4">
						{/* Servicios con submenú móvil */}
						<div>
							<button
								onClick={() => setShowServicesDropdown(!showServicesDropdown)}
								className="w-full flex items-center justify-between px-4 py-3 text-left text-sm font-medium text-gray-700 hover:bg-gray-50 active:bg-gray-100 rounded-lg transition-all duration-200 touch-manipulation hover:text-primary"
							>
								<span>Servicios</span>
								<svg 
									className={`w-5 h-5 transition-transform duration-200 ${showServicesDropdown ? 'rotate-180' : ''}`} 
									fill="none" 
									stroke="currentColor" 
									viewBox="0 0 24 24"
								>
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
								</svg>
							</button>
							{showServicesDropdown && (
								<div className="pl-4 mt-1 space-y-1 max-h-64 overflow-y-auto">
									{serviciosList.map((service, index) => (
										<button
											key={index}
											onClick={() => {
												handleServiceClick(service);
												setShowMobileMenu(false);
												setShowServicesDropdown(false);
											}}
											className="w-full text-left px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 active:bg-gray-100 rounded-lg transition-colors touch-manipulation"
										>
											{service.title}
										</button>
									))}
									<button
										onClick={() => {
											handleNavClick('servicios');
											setShowMobileMenu(false);
											setShowServicesDropdown(false);
										}}
										className="w-full text-left px-4 py-2 text-sm font-medium text-primary hover:bg-gray-50 active:bg-gray-100 rounded-lg transition-colors touch-manipulation"
									>
										Ver todos los servicios →
									</button>
								</div>
							)}
						</div>
						
						<button 
							onClick={() => {
								handleNavClick('casos');
								setShowMobileMenu(false);
							}} 
							className="w-full text-left px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 active:bg-gray-100 rounded-lg transition-colors touch-manipulation"
						>
							Casos
						</button>
						<button 
							onClick={() => {
								handleNavClick('nosotros');
								setShowMobileMenu(false);
							}} 
							className="w-full text-left px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 active:bg-gray-100 rounded-lg transition-colors touch-manipulation"
						>
							Nosotros
						</button>
						<Link 
							to="/blog" 
							onClick={() => setShowMobileMenu(false)}
							className="block w-full text-left px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 active:bg-gray-100 rounded-lg transition-colors touch-manipulation"
						>
							Blog
						</Link>
						<Link 
							to="/referidores" 
							onClick={() => setShowMobileMenu(false)}
							className="block w-full text-left px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 active:bg-gray-100 rounded-lg transition-colors touch-manipulation"
						>
							Referidores
						</Link>
						<Link
							to="/empresas-en-carpeta"
							onClick={() => setShowMobileMenu(false)}
							className="block w-full text-left px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 active:bg-gray-100 rounded-lg transition-colors touch-manipulation"
						>
							Empresas en Carpeta
						</Link>
						<Link
							to="/vender-empresa"
							onClick={() => setShowMobileMenu(false)}
							className="block w-full text-left px-4 py-3 text-sm font-semibold text-secondary hover:bg-gray-50 active:bg-gray-100 rounded-lg transition-colors touch-manipulation"
						>
							Vender mi empresa
						</Link>
						<button 
							onClick={() => {
								handleNavClick('contacto');
								setShowMobileMenu(false);
							}} 
							className="w-full text-left px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 active:bg-gray-100 rounded-lg transition-colors touch-manipulation"
						>
							Contacto
						</button>
						
						{/* Botones de acción móvil */}
						<div className="pt-2 border-t mt-2 space-y-2">
							{user ? (
								<>
									{/* Información del usuario móvil mejorada */}
									<div className="px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-primary/5 to-secondary/5">
										<div className="flex items-center gap-3">
											<div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
												<UserCircleIcon className="h-6 w-6 text-primary" />
											</div>
											<div className="flex-1 min-w-0">
												<p className="text-sm font-semibold text-gray-900 truncate">
													{user.first_name && user.last_name 
														? `${user.first_name} ${user.last_name}`
														: user.username}
												</p>
												{user.email && (
													<p className="text-xs text-gray-500 truncate">{user.email}</p>
												)}
												{user.profile && (
													<span className={`inline-block mt-1 px-2 py-0.5 text-xs font-medium rounded-full ${
														user.profile.role === 'admin' ? 'bg-red-100 text-red-800' :
														user.profile.role === 'gestor' ? 'bg-blue-100 text-blue-800' :
														'bg-green-100 text-green-800'
													}`}>
														{user.profile.role_display || user.profile.role}
													</span>
												)}
											</div>
										</div>
									</div>
									<div className="px-4 space-y-0.5 pb-1">
										<Link
											to="/"
											onClick={() => setShowMobileMenu(false)}
											className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 active:bg-gray-100"
										>
											<HomeIcon className="h-5 w-5 text-blue-600 shrink-0" />
											Página Principal
										</Link>
										<Link
											to="/admin/dashboard"
											onClick={() => setShowMobileMenu(false)}
											className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 active:bg-gray-100"
										>
											<ShieldCheckIcon className="h-5 w-5 text-primary shrink-0" />
											Inicio administrativo
										</Link>
										<Link
											to="/dgii"
											onClick={() => setShowMobileMenu(false)}
											className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 active:bg-gray-100"
										>
											<BanknotesIcon className="h-5 w-5 text-green-600 shrink-0" />
											Formularios DGII
										</Link>
										{isGestor() && (
											<Link
												to="/dgii/admin"
												onClick={() => setShowMobileMenu(false)}
												className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 active:bg-gray-100"
											>
												<ChartPieIcon className="h-5 w-5 text-blue-600 shrink-0" />
												Admin DGII
											</Link>
										)}
										{isAdmin() && (
											<Link
												to="/admin/users"
												onClick={() => setShowMobileMenu(false)}
												className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 active:bg-gray-100"
											>
												<ShieldCheckIcon className="h-5 w-5 text-purple-600 shrink-0" />
												Gestión de Usuarios
											</Link>
										)}
									</div>
									<button 
										type="button"
										onClick={() => {
											handleLogout();
											setShowMobileMenu(false);
										}} 
										className="w-full px-4 py-3 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 active:bg-red-800 transition-colors touch-manipulation flex items-center justify-center gap-2"
									>
										<ArrowRightOnRectangleIcon className="h-5 w-5" />
										Cerrar Sesión
									</button>
								</>
							) : (
								<Link 
									to="/login" 
									onClick={() => setShowMobileMenu(false)}
									className="block w-full px-4 py-3 bg-primary text-white rounded-lg text-sm font-medium text-center hover:opacity-90 active:opacity-80 transition-opacity touch-manipulation"
								>
									Iniciar Sesión
								</Link>
							)}
						</div>
					</div>
				</div>
			)}
		</nav>
	);
}

function Hero() {
    const { data: heroes = [], isLoading } = useQuery({
        queryKey: ['heroes'],
        queryFn: contentApi.getActiveHeroes,
        retry: 1,
        staleTime: 5 * 60 * 1000,
    });

    const [current, setCurrent] = useState(0);
    const [paused, setPaused] = useState(false);

    // rotación cada 6s si hay múltiples heroes
    useEffect(() => {
        if (!heroes || heroes.length <= 1 || paused) return;
        const id = setInterval(() => {
            setCurrent((prev) => (prev + 1) % heroes.length);
        }, 6000);
        return () => clearInterval(id);
    }, [heroes, paused]);

	const defaultHero = {
		content_type: 'text',
		title: 'Más que asesores, somos tus aliados.',
		subtitle: 'Soluciones integrales a nivel nacional en contabilidad, finanzas, fiscalidad y administración para impulsar tu crecimiento.',
		cta_primary_text: 'Solicitar asesoría',
		cta_primary_link: '#contacto',
		cta_secondary_text: 'Ver servicios',
		cta_secondary_link: '#servicios'
	};

    const heroList = (heroes && heroes.length > 0) ? heroes : [defaultHero];
    const heroData = heroList[Math.min(current, heroList.length - 1)];
	const contentType = heroData.content_type || 'text';

    if (isLoading && heroes.length === 0) {
		return (
			<section className="text-white bg-gradient-to-br from-primary to-secondary">
				<div className="container-width py-20 sm:py-28">
					<div className="animate-pulse">
						<div className="h-12 bg-white/20 rounded w-3/4 mb-4"></div>
						<div className="h-6 bg-white/20 rounded w-1/2"></div>
					</div>
				</div>
			</section>
		);
	}

    const goPrev = () => setCurrent((prev) => (prev - 1 + heroList.length) % heroList.length);
    const goNext = () => setCurrent((prev) => (prev + 1) % heroList.length);

	// Función para renderizar el contenido según el tipo
	const renderHeroContent = () => {
		switch (contentType) {
			case 'image':
				if (heroData.image_url) {
					const imageContent = (
						<div key={current} className="slide-x-in w-full flex items-center justify-center relative z-20">
							{heroData.image_link ? (
								<a href={heroData.image_link} target="_blank" rel="noreferrer" className="block w-full max-w-full">
									<img 
										src={heroData.image_url} 
										alt={heroData.image_alt || heroData.title || 'Banner'} 
										className="w-full h-auto max-h-[36vh] sm:max-h-[42vh] lg:max-h-[45vh] object-contain drop-shadow-lg"
									/>
								</a>
							) : (
								<img 
									src={heroData.image_url} 
									alt={heroData.image_alt || heroData.title || 'Banner'} 
									className="w-full h-auto max-h-[36vh] sm:max-h-[42vh] lg:max-h-[45vh] object-contain drop-shadow-lg"
								/>
							)}
						</div>
					);
					return imageContent;
				}
				break;
			
			case 'html':
				if (heroData.html_content) {
					return (
						<div 
							key={current} 
							className="slide-x-in w-full flex items-center justify-center relative z-20 hero-html-container"
							dangerouslySetInnerHTML={{ __html: heroData.html_content }}
						/>
					);
				}
				break;
			
			case 'ad':
				if (heroData.ad_banner_url || heroData.ad_script) {
					return (
						<div key={current} className="slide-x-in w-full flex items-center justify-center relative z-20">
							{heroData.ad_script && (
								<div dangerouslySetInnerHTML={{ __html: heroData.ad_script }} />
							)}
							{heroData.ad_banner_url && (
								heroData.ad_link ? (
									<a href={heroData.ad_link} target="_blank" rel="noreferrer" className="block w-full max-w-full">
										<img 
											src={heroData.ad_banner_url} 
											alt={heroData.ad_alt || 'Publicidad'} 
											className="w-full h-auto max-h-[36vh] sm:max-h-[42vh] lg:max-h-[45vh] object-contain drop-shadow-lg"
										/>
									</a>
								) : (
									<img 
										src={heroData.ad_banner_url} 
										alt={heroData.ad_alt || 'Publicidad'} 
										className="w-full h-auto max-h-[36vh] sm:max-h-[42vh] lg:max-h-[45vh] object-contain drop-shadow-lg"
									/>
								)
							)}
						</div>
					);
				}
				break;
			
			case 'text':
			default:
				return (
					<div key={current} className="slide-x-in relative z-20">
						{heroData.title && (
							<h1 className="text-2xl sm:text-4xl lg:text-6xl font-extrabold leading-tight drop-shadow px-2 sm:px-0">{heroData.title}</h1>
						)}
						{heroData.subtitle && (
							<p className="mt-3 sm:mt-5 text-white/90 text-sm sm:text-lg max-w-2xl px-2 sm:px-0">{heroData.subtitle}</p>
						)}
					</div>
				);
		}
		return null;
	};

	const isFullWidth = ['image', 'html', 'ad'].includes(contentType);
	// Siempre mostrar el fondo de gradiente como base
	const hasBackground = true; // El fondo siempre está presente

    return (
        <section
            className="relative text-white bg-gradient-to-br from-primary to-secondary"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            onTouchStart={() => setPaused(true)}
            onTouchEnd={() => setPaused(false)}
        >
            {/* Fondo de gradiente siempre visible */}
            <div className="absolute inset-0 bg-gradient-to-br from-primary to-secondary z-0"></div>
            
            {/* Contenido sobre el fondo */}
            <div className={`relative z-10 container-width py-8 sm:py-16 lg:py-28 min-h-[36vh] sm:min-h-[42vh] lg:min-h-[45vh] max-h-[36vh] sm:max-h-[42vh] lg:max-h-[45vh] ${isFullWidth ? 'flex items-center justify-center' : 'grid lg:grid-cols-2 gap-10 items-center'}`}>
				{renderHeroContent()}
                {!isFullWidth && <div className="hidden lg:block" />}
            </div>

            {heroList.length > 1 && (
                <>
                    {/* Controles */}
                    <button 
                        type="button"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            goPrev();
                        }} 
                        aria-label="Anterior" 
                        className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 text-white rounded-full w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center z-50 pointer-events-auto transition-all cursor-pointer"
                        style={{ zIndex: 50 }}
                    >
                        <span className="sr-only">Anterior</span>
                        <svg className="w-4 h-4 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                    <button 
                        type="button"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            goNext();
                        }} 
                        aria-label="Siguiente" 
                        className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 text-white rounded-full w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center z-50 pointer-events-auto transition-all cursor-pointer"
                        style={{ zIndex: 50 }}
                    >
                        <span className="sr-only">Siguiente</span>
                        <svg className="w-4 h-4 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                    </button>

                    {/* Paginación */}
                    <div className="absolute left-1/2 -translate-x-1/2 bottom-3 sm:bottom-6 flex gap-1.5 sm:gap-2">
                        {heroList.map((_, i) => (
                            <button key={i} aria-label={`Ir al slide ${i + 1}`} onClick={() => setCurrent(i)} className={`h-2 w-2 sm:h-2.5 sm:w-2.5 rounded-full ${i === current ? 'bg-white' : 'bg-white/50 hover:bg-white/70'}`}></button>
                        ))}
                    </div>
                </>
            )}
        </section>
    );
}

function Section({ id, title, children }) {
	return (
		<section id={id} className="container-width py-16">
			<h2 className="text-3xl sm:text-4xl font-bold mb-6 text-primary">{title}</h2>
			<div className="prose max-w-none">{children}</div>
		</section>
	);
}

function ServiceModal({ isOpen, onClose, service }) {
	// Valores por defecto de los formularios (fallback si la API falla)
	const DEFAULT_FORMS = {
		form_url_srl_eirl: { type: 'form', value: 'https://forms.office.com/r/j69yhJDKnn?origin=lprLink' },
		form_url_persona_fisica: { type: 'form', value: 'https://forms.office.com/r/nLNZXjQSyx?origin=lprLink' },
		form_url_ong: { type: 'form', value: 'https://forms.office.com/r/JHyy9NyvLt' },
		form_url_empresas_carpeta: { type: 'form', value: 'https://forms.office.com/r/D1gfUZmn5J?origin=lprLink' },
		form_url_outsourcing_igualas: { type: 'form', value: 'https://forms.office.com/r/D1gfUZmn5J?origin=lprLink' },
		form_url_general: { type: 'form', value: 'https://zfrmz.com/jL0O5LTnIFCIT5PK5BHJ' },
		form_url_constitucion: { type: 'form', value: 'https://zfrmz.com/jL0O5LTnIFCIT5PK5BHJ' },
		form_url_gestiones_legales: { type: 'form', value: 'https://zfrmz.com/jL0O5LTnIFCIT5PK5BHJ' },
		form_url_renovacion_mercantil: { type: 'form', value: 'https://zfrmz.com/jL0O5LTnIFCIT5PK5BHJ' },
		form_url_asesoria_fiscal: { type: 'form', value: 'https://zfrmz.com/jL0O5LTnIFCIT5PK5BHJ' },
		form_url_estados_financieros: { type: 'form', value: 'https://zfrmz.com/jL0O5LTnIFCIT5PK5BHJ' },
		form_url_auditorias: { type: 'form', value: 'https://zfrmz.com/jL0O5LTnIFCIT5PK5BHJ' },
		form_url_inventarios: { type: 'form', value: 'https://zfrmz.com/jL0O5LTnIFCIT5PK5BHJ' },
		form_url_imagen_corporativa: { type: 'form', value: 'https://zfrmz.com/jL0O5LTnIFCIT5PK5BHJ' },
		form_url_diseno_logo: { type: 'form', value: 'https://zfrmz.com/jL0O5LTnIFCIT5PK5BHJ' },
		form_url_elaboracion_sellos: { type: 'form', value: 'https://zfrmz.com/jL0O5LTnIFCIT5PK5BHJ' },
		form_url_asesoria_financiera: { type: 'form', value: 'https://zfrmz.com/jL0O5LTnIFCIT5PK5BHJ' },
		form_url_redes_sociales: { type: 'form', value: 'https://zfrmz.com/jL0O5LTnIFCIT5PK5BHJ' },
		form_url_pautas_tv: { type: 'form', value: 'https://zfrmz.com/jL0O5LTnIFCIT5PK5BHJ' },
		form_url_posicionamiento_marca: { type: 'form', value: 'https://zfrmz.com/jL0O5LTnIFCIT5PK5BHJ' },
		form_url_centros_salud: { type: 'form', value: 'https://zfrmz.com/jL0O5LTnIFCIT5PK5BHJ' },
		form_url_registro_marca: { type: 'form', value: 'https://forms.office.com/r/rA7WFgMDEp' },
		form_url_facturacion_electronica: { type: 'form', value: 'https://zfrmz.com/jL0O5LTnIFCIT5PK5BHJ' },
	};

	// Estado para los links de formularios (ahora con tipo de acción)
	const [formUrls, setFormUrls] = useState(DEFAULT_FORMS);
	const [loadingForms, setLoadingForms] = useState(true);
	
	// Estado para precios y moneda
	const [planPrices, setPlanPrices] = useState({
		currency: 'RD$',
		ong: {},
		srl: {},
		eirl: {},
		persona_fisica: {}
	});
	const [loadingPrices, setLoadingPrices] = useState(true);
	
	// Función helper para formatear precio con moneda
	const formatPrice = (amount) => {
		if (!amount) return '';
		const currency = planPrices.currency || 'RD$';
		return `${currency}${amount.toLocaleString('es-DO')}`;
	};

	// Función helper para obtener el link final según el tipo de acción
	const getActionLink = (config) => {
		if (!config || !config.value) return null;
		
		if (config.type === 'whatsapp') {
			// Formato: https://wa.me/numero (sin + ni espacios)
			const number = config.value.replace(/[^\d]/g, '');
			return `https://wa.me/${number}`;
		} else if (config.type === 'social') {
			// URL directa de red social
			return config.value;
		} else {
			// Formulario (por defecto)
			return config.value;
		}
	};

	// Función helper para obtener el texto del botón según el tipo
	const getActionButtonText = (config) => {
		if (!config || !config.value) return 'Adquirir Plan';
		
		if (config.type === 'whatsapp') {
			return 'Contactar por WhatsApp';
		} else if (config.type === 'social') {
			return 'Seguir en Red Social';
		} else {
			return 'Adquirir Plan';
		}
	};
	
	// Función helper para obtener precio formateado según tipo de plan y key
	const getPlanPrice = (planType, priceKey) => {
		if (!planPrices[planType] || !planPrices[planType][priceKey]) return null;
		const priceData = planPrices[planType][priceKey];
		return priceData.price ? formatPrice(priceData.price) : null;
	};
	
	// Función helper para obtener avance formateado según tipo de plan y key
	const getPlanAdvance = (planType, priceKey) => {
		if (!planPrices[planType] || !planPrices[planType][priceKey]) return null;
		const priceData = planPrices[planType][priceKey];
		return priceData.advance ? formatPrice(priceData.advance) : null;
	};

	// Estado para las pestañas de Constitución (hooks deben estar antes de cualquier return)
	const [activeTab, setActiveTab] = useState('SRL');

	// Cargar links de formularios y precios desde la API
	useEffect(() => {
		const loadData = async () => {
			try {
				// Cargar links de formularios
				const urls = await contentApi.getServiceForms();
				// Normalizar el formato: si viene como string (legacy), convertir a objeto
				const normalized = {};
				Object.keys(DEFAULT_FORMS).forEach(key => {
					if (urls[key]) {
						if (typeof urls[key] === 'object' && urls[key].type && urls[key].value !== undefined) {
							normalized[key] = urls[key];
						} else {
							// Formato legacy: solo string
							normalized[key] = { type: 'form', value: urls[key] };
						}
					} else {
						normalized[key] = DEFAULT_FORMS[key];
					}
				});
				setFormUrls(normalized);
				
				// Cargar precios
				const prices = await contentApi.getPlanPrices();
				setPlanPrices(prices);
			} catch (error) {
				console.warn('Error cargando datos, usando valores por defecto:', error);
				// Mantener valores por defecto si falla
			} finally {
				setLoadingForms(false);
				setLoadingPrices(false);
			}
		};

		if (isOpen) {
			loadData();
		}
	}, [isOpen]);

	// Resetear pestaña cuando se abre el modal
	useEffect(() => {
		if (isOpen && service?.title === 'Constitución de Empresas & ONG') {
			setActiveTab('SRL');
		}
	}, [isOpen, service]);

	if (!isOpen || !service) return null;

	// Planes para ONG/Fundaciones
	const planesONG = [
		{
			name: 'Básico',
			desc: 'Plan esencial para registro de fundaciones y ONG',
			priceKey: 'basico',
			linkKey: 'form_url_ong',
			features: [
				'Nombre Comercial',
				'Relación de membresía de los miembros',
				'Nómina de presencia de los miembros',
				'Acta de asamblea general constitutiva',
				'Estatutos de la asociación',
				'Certificación de incorporación por el Ministerio Público',
				'Resolución de incorporación por el Ministerio Público',
				'Certificación emitida por el Ayuntamiento',
				'Certificación emitida por el Juzgado de Paz',
				'Certificación emitida por la Cámara Civil',
				'Registro Nacional del Contribuyente (RNC)'
			]
		},
		{
			name: 'Premium',
			desc: 'Incluye acceso a oficina virtual de DGII',
			priceKey: 'premium',
			linkKey: 'form_url_ong',
			features: [
				'Todo lo incluido en Plan Básico',
				'Creación de acceso a la oficina virtual de la DGII'
			]
		},
		{
			name: 'Ultra',
			desc: 'Incluye gestión de comprobantes fiscales',
			priceKey: 'ultra',
			linkKey: 'form_url_ong',
			features: [
				'Todo lo incluido en Plan Premium',
				'Solicitud y desbloqueo de números de comprobantes fiscales'
			]
		},
		{
			name: 'Elite',
			desc: 'Solución completa con registro TSS e identidad visual',
			priceKey: 'elite',
			linkKey: 'form_url_ong',
			features: [
				'Todo lo incluido en Plan Ultra',
				'Registro de Acceso en Oficina Virtual de la TSS',
				'Identidad Visual'
			]
		}
	];

	// Planes para SRL
	const planesSRL = [
		{
			name: 'Básico',
			desc: 'Plan esencial para registro de SRL',
			priceKey: 'basico',
			linkKey: 'form_url_srl_eirl',
			features: [
				'Nombre comercial',
				'Nómina y Acta de Asamblea Constitutiva',
				'Estatutos Sociales',
				'Registro mercantil',
				'Creación de Acceso a la Oficina Virtual (DGII)',
				'Certificación de Inscripción al RNC',
				'Solicitud de Alta de Comprobantes Fiscales',
				'Creación de Acceso a la Virtual de la TSS',
				'Creación de Acceso a la Virtual del MT',
				'Sello Gomigrafo'
			]
		},
		{
			name: 'Premium',
			desc: 'Incluye asistencia en apertura de cuenta bancaria',
			priceKey: 'premium',
			linkKey: 'form_url_srl_eirl',
			features: [
				'Todo lo incluido en Plan Básico',
				'Asistencia en Apertura de Cuenta Bancaria'
			]
		},
		{
			name: 'Ultra',
			desc: 'Incluye registro como importador y exportador',
			priceKey: 'ultra',
			linkKey: 'form_url_srl_eirl',
			features: [
				'Todo lo incluido en Plan Premium',
				'Registro de Proveedor del Estado (RPE)',
				'Registro como Importador/Exportador (DGA)'
			]
		},
		{
			name: 'Elite',
			desc: 'Solución completa con facturador electrónico',
			priceKey: 'elite',
			linkKey: 'form_url_srl_eirl',
			features: [
				'Todo lo incluido en Plan Ultra',
				'Facturador Electrónico'
			]
		}
	];

	// Planes para EIRL
	const planesEIRL = [
		{
			name: 'Básico',
			desc: 'Plan esencial para registro de EIRL',
			priceKey: 'basico',
			linkKey: 'form_url_srl_eirl',
			features: [
				'Nombre comercial',
				'Acto Constitutivo Notarizado',
				'Registro mercantil',
				'Creación de Acceso a la Oficina Virtual (DGII)',
				'Certificación de Inscripción al RNC',
				'Solicitud de Alta de Comprobantes Fiscales',
				'Creación de Acceso a la Virtual de la TSS',
				'Creación de Acceso a la Virtual del MT',
				'Sello Gomigrafo'
			]
		},
		{
			name: 'Premium',
			desc: 'Incluye asistencia en apertura de cuenta bancaria',
			priceKey: 'premium',
			linkKey: 'form_url_srl_eirl',
			features: [
				'Todo lo incluido en Plan Básico',
				'Asistencia en Apertura de Cuenta Bancaria'
			]
		},
		{
			name: 'Ultra',
			desc: 'Incluye registro como importador y exportador',
			priceKey: 'ultra',
			linkKey: 'form_url_srl_eirl',
			features: [
				'Todo lo incluido en Plan Premium',
				'Registro de Proveedor del Estado (RPE)',
				'Registro como Importador/Exportador (DGA)'
			]
		},
		{
			name: 'Elite',
			desc: 'Solución completa con facturador electrónico',
			priceKey: 'elite',
			linkKey: 'form_url_srl_eirl',
			features: [
				'Todo lo incluido en Plan Ultra',
				'Facturador Electrónico'
			]
		}
	];

	// Planes para Persona Física
	const planesPersonaFisica = [
		{
			name: 'Básico',
			desc: 'Plan esencial para registro de Persona Física',
			priceKey: 'basico',
			linkKey: 'form_url_persona_fisica',
			features: [
				'Registro Nacional del Contribuyente (RNC)',
				'Creación de acceso a la oficina virtual de la DGII',
				'Certificación de RNC',
				'Solicitud y desbloqueo de números de comprobantes fiscales',
				'Formato de Facturas con Comprobantes Fiscales',
				'Sello Gomígrafo'
			]
		},
		{
			name: 'Premium',
			desc: 'Incluye registro en TSS y nombre comercial',
			price: 'RD$15,500',
			advance: 'RD$10,000',
			linkKey: 'form_url_persona_fisica',
			features: [
				'Todo lo incluido en Plan Básico',
				'Registro de Acceso en Oficina Virtual de la TSS',
				'Nombre comercial',
				'Formato de Facturas con Comprobantes Fiscales'
			]
		},
		{
			name: 'Ultra',
			desc: 'Solución completa con registro como proveedor del estado',
			priceKey: 'ultra',
			linkKey: 'form_url_persona_fisica',
			features: [
				'Todo lo incluido en Plan Premium',
				'Asistencia en Apertura de Cuenta Bancaria',
				'Registro de Proveedor del Estado'
			]
		}
	];

	// Servicios complementarios (común para todos los tipos)
	const serviciosComplementarios = [
		{ name: 'Diseño de Logo simple', price: 'RD$1,500' },
		{ name: 'Diseño de Logo Profesional', price: 'RD$4,000' },
		{ name: 'Identidad Visual', price: 'RD$8,000' }
	];

	// Función para obtener los planes según el tab activo, con precios dinámicos aplicados
	const getCurrentPlans = () => {
		let plans = [];
		let planType = '';
		
		switch (activeTab) {
			case 'SRL':
				plans = planesSRL;
				planType = 'srl';
				break;
			case 'EIRL':
				plans = planesEIRL;
				planType = 'eirl';
				break;
			case 'ONG':
				plans = planesONG;
				planType = 'ong';
				break;
			case 'Persona Física':
				plans = planesPersonaFisica;
				planType = 'persona_fisica';
				break;
			default:
				plans = planesONG;
				planType = 'ong';
		}
		
		// Aplicar precios dinámicos a los planes
		return plans.map(plan => {
			if (plan.priceKey) {
				return {
					...plan,
					price: getPlanPrice(planType, plan.priceKey),
					advance: getPlanAdvance(planType, plan.priceKey)
				};
			}
			return plan;
		});
	};

	// Planes de Outsourcing contable y fiscal (usando linkKey para configuración dinámica)
	const planesIgualas = [
		{
			name: 'Básico',
			desc: 'Declaración en 0 - Ideal para cumplimientos esenciales',
			linkKey: 'form_url_outsourcing_igualas',
			features: [
				'Declaración Jurada IR-3',
				'Formatos de envío (606, 607 y 608)',
				'Declaración Jurada de ITBIS (IT-1)'
			]
		},
		{
			name: 'PYME',
			desc: 'Solución completa para pequeñas y medianas empresas',
			linkKey: 'form_url_outsourcing_igualas',
			features: [
				'Todo lo incluido en Plan Básico',
				'Asesoría Financiera',
				'Asesoría Fiscal',
				'Gestión Tesorería de la Seguridad Social (TSS)',
				'Gestión INFOTEP',
				'Elaboración de Nómina',
				'Declaración Jurada IR-17',
				'Anticipos de ISR'
			]
		},
		{
			name: 'PRO',
			desc: 'Solución integral con gestión contable completa',
			linkKey: 'form_url_outsourcing_igualas',
			features: [
				'Todo lo incluido en Plan PYME',
				'Reporte de situación fiscal',
				'Registro contable de todas las operaciones de la empresa',
				'Actualización de cuentas por cobrar / por pagar',
				'Conciliación de débito de cuentas bancarias y tarjeta de crédito',
				'Elaboración de Estados Financieros para uso interno'
			]
		},
		{
			name: 'PLUS',
			desc: 'Solución completa con asesoría en financiamiento',
			linkKey: 'form_url_outsourcing_igualas',
			features: [
				'Todo lo incluido en Plan PRO',
				'Asesoría en gestión de financiamiento'
			]
		}
	];

	const planesRedes = [
		{
			name: 'Básico',
			desc: 'Plan esencial para iniciar en redes sociales',
			price: 'RD$6,435.00',
			features: [
				'Análisis de tu situación actual',
				'Estrategia de contenido personalizada',
				'Monitoreo y gestión básica de interacciones',
				'3 publicaciones semanales en dos redes sociales'
			]
		},
		{
			name: 'Estándar',
			desc: 'Plan más popular - Ideal para crecimiento',
			price: 'RD$13,455.00',
			popular: true,
			features: [
				'Todo lo incluido en Plan Básico',
				'Informes mensuales de rendimiento',
				'Estrategias de crecimiento',
				'Optimización de perfiles'
			]
		},
		{
			name: 'Premium',
			desc: 'Solución completa con publicidad',
			price: 'RD$31,590.00',
			features: [
				'Todo lo incluido en Plan Estándar',
				'4 publicaciones semanales en dos redes sociales',
				'Campañas publicitarias estratégicas (USD$100 incluidos)',
				'Informes avanzados de rendimiento'
			]
		}
	];

	const planesLogos = [
		{
			name: 'Básico',
			desc: 'Ideal para emprendimientos que están comenzando',
			price: 'RD$2,047.50',
			features: [
				'2 rondas de cambios',
				'Entrega en editables, JPG, PNG'
			]
		},
		{
			name: 'Estándar',
			desc: 'Pensado para marcas que necesitan más flexibilidad visual',
			price: 'RD$3,802.50',
			features: [
				'Manual de identidad visual (breve)',
				'Usos correctos/incorrectos',
				'Tamaños mínimos',
				'Colores',
				'2 rondas de cambios',
				'Entrega en editables, JPG, PNG'
			]
		},
		{
			name: 'Premium',
			desc: 'Recomendado para marcas que necesitan mayor impulso y proyección visual',
			price: 'RD$7,312.50',
			features: [
				'Naming y concepto del logo',
				'Manual de identidad visual completo',
				'Psicología del color',
				'Construcción del logo',
				'Espacio de seguridad',
				'Proporciones',
				'Fuentes',
				'Paleta de color',
				'Usos incorrectos',
				'Entrega en editables, JPG, PNG'
			]
		}
	];

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
			<div className="fixed inset-0 bg-black/50" aria-hidden="true" />
			<div className="relative bg-white rounded-xl shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
				<button onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-slate-600">
					<XMarkIcon className="w-6 h-6" />
				</button>
				<div className="p-6">
					<h2 className="text-3xl font-bold text-primary mb-4">{service.title}</h2>
					{service.title === 'Constitución de Empresas & ONG' ? (
						<>
							<p className="text-slate-700 mb-6">Ofrecemos planes especializados adaptados a tus necesidades según el tipo de empresa que deseas constituir. Selecciona el tipo de empresa para ver los planes disponibles.</p>
							
							{/* Pestañas */}
							<div className="flex flex-wrap gap-2 mb-6 border-b border-slate-200">
								{['SRL', 'EIRL', 'ONG', 'Persona Física'].map((tab) => (
									<button
										key={tab}
										onClick={() => setActiveTab(tab)}
										className={`px-6 py-3 font-semibold transition-all relative ${
											activeTab === tab
												? 'text-primary border-b-2 border-primary'
												: 'text-slate-600 hover:text-primary'
										}`}
									>
										{tab}
										{activeTab === tab && (
											<span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"></span>
										)}
									</button>
								))}
							</div>

							{/* Servicios Complementarios */}
							<div className="bg-secondary/10 border-l-4 border-secondary p-4 mb-6 rounded">
								<h4 className="font-semibold text-primary mb-2">Servicios Complementarios:</h4>
								<ul className="text-sm text-slate-700 space-y-1">
									{serviciosComplementarios.map((servicio, idx) => (
										<li key={idx}>• {servicio.name}: {servicio.price}</li>
									))}
								</ul>
							</div>

							{/* Planes según la pestaña activa */}
							<div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
								{getCurrentPlans().map((plan) => (
									<div key={plan.name} className="card p-6 border-l-4 flex flex-col" style={{ borderLeftColor: '#12284C' }}>
										<h3 className="font-bold text-xl text-primary mb-2">{plan.name}</h3>
										<p className="text-sm text-slate-600 mb-3">{plan.desc}</p>
										{plan.price && (
											<div className="mb-4 pb-4 border-b border-slate-200">
												<div className="flex flex-col gap-1">
													<div className="flex items-baseline gap-2">
														<span className="text-xs text-slate-500">Precio:</span>
														<span className="text-lg font-bold text-primary">{plan.price}</span>
													</div>
													{plan.advance && (
														<div className="flex items-baseline gap-2">
															<span className="text-xs text-slate-500">Avance:</span>
															<span className="text-sm font-semibold text-slate-700">{plan.advance}</span>
														</div>
													)}
												</div>
											</div>
										)}
										<ul className="space-y-2 mb-6 text-sm text-slate-700 flex-grow">
											{plan.features.map((f, i) => (
												<li key={i} className="flex items-start">
													<span className="text-secondary mr-2">✓</span>
													<span>{f}</span>
												</li>
											))}
										</ul>
										{(() => {
											// Obtener la configuración del link del plan
											let actionConfig = null;
											if (plan.linkKey && formUrls[plan.linkKey]) {
												actionConfig = formUrls[plan.linkKey];
											} else if (plan.link) {
												// Si plan.link es un string (legacy), convertir
												actionConfig = { type: 'form', value: plan.link };
											}
											
											const actionLink = actionConfig ? getActionLink(actionConfig) : null;
											const buttonText = actionConfig ? getActionButtonText(actionConfig) : 'Adquirir Plan';
											
											return actionLink ? (
												<a 
													href={actionLink} 
													target="_blank" 
													rel="noreferrer" 
													className="btn-primary w-full text-center block mt-auto"
												>
													{buttonText}
												</a>
											) : (
												<button className="btn-primary w-full text-center mt-auto" disabled>
													{buttonText}
												</button>
											);
										})()}
									</div>
								))}
							</div>
						</>
					) : service.title === 'Outsourcing contable y fiscal' ? (
						<>
							<p className="text-slate-700 mb-6">{service.details}</p>
							<div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
								{planesIgualas.map((plan) => (
									<div key={plan.name} className="card p-6 border-l-4 flex flex-col" style={{ borderLeftColor: '#12284C' }}>
										<h3 className="font-bold text-xl text-primary mb-2">{plan.name}</h3>
										<p className="text-sm text-slate-600 mb-4">{plan.desc}</p>
										<ul className="space-y-2 mb-6 text-sm text-slate-700 flex-grow">
											{plan.features.map((f, i) => (
												<li key={i} className="flex items-start">
													<span className="text-secondary mr-2">✓</span>
													<span>{f}</span>
												</li>
											))}
										</ul>
										{(() => {
											// Obtener la configuración del link del plan
											let actionConfig = null;
											if (plan.linkKey && formUrls[plan.linkKey]) {
												actionConfig = formUrls[plan.linkKey];
											} else if (plan.link) {
												// Si plan.link es un string (legacy), convertir
												actionConfig = { type: 'form', value: plan.link };
											}
											
											const actionLink = actionConfig ? getActionLink(actionConfig) : null;
											const buttonText = actionConfig ? getActionButtonText(actionConfig) : 'Adquirir Plan';
											
											return actionLink ? (
												<a 
													href={actionLink} 
													target="_blank" 
													rel="noreferrer" 
													className="btn-primary w-full text-center block mt-auto"
												>
													{buttonText}
												</a>
											) : (
												<button className="btn-primary w-full text-center mt-auto" disabled>
													{buttonText}
												</button>
											);
										})()}
									</div>
								))}
							</div>
						</>
					) : service.title === 'Manejo de Redes Sociales' ? (
						<>
							<p className="text-slate-700 mb-6">{service.details}</p>
							<div className="grid md:grid-cols-3 gap-6 mb-6">
								{planesRedes.map((plan) => (
									<div key={plan.name} className={`card p-6 border-l-4 flex flex-col relative ${plan.popular ? 'border-secondary ring-2 ring-secondary/20' : ''}`} style={{ borderLeftColor: plan.popular ? '#44797B' : '#12284C' }}>
										{plan.popular && (
											<div className="absolute -top-3 right-4 bg-secondary text-white text-xs font-bold px-3 py-1 rounded-full">
												MÁS POPULAR
											</div>
										)}
										<h3 className="font-bold text-xl text-primary mb-2">{plan.name}</h3>
										<p className="text-sm text-slate-600 mb-3">{plan.desc}</p>
										{plan.price && (
											<div className="mb-4 pb-4 border-b border-slate-200">
												<div className="flex items-baseline gap-2">
													<span className="text-xs text-slate-500">Precio:</span>
													<span className="text-lg font-bold text-primary">{plan.price}</span>
												</div>
											</div>
										)}
										<ul className="space-y-2 mb-6 text-sm text-slate-700 flex-grow">
											{plan.features.map((f, i) => (
												<li key={i} className="flex items-start">
													<span className="text-secondary mr-2">✓</span>
													<span>{f}</span>
												</li>
											))}
										</ul>
										{(() => {
											// Obtener la configuración del link del plan
											let actionConfig = null;
											if (plan.linkKey && formUrls[plan.linkKey]) {
												actionConfig = formUrls[plan.linkKey];
											} else if (plan.link) {
												// Si plan.link es un string (legacy), convertir
												actionConfig = { type: 'form', value: plan.link };
											}
											
											const actionLink = actionConfig ? getActionLink(actionConfig) : null;
											const buttonText = actionConfig ? getActionButtonText(actionConfig) : 'Adquirir Plan';
											
											return actionLink ? (
												<a 
													href={actionLink} 
													target="_blank" 
													rel="noreferrer" 
													className="btn-primary w-full text-center block mt-auto"
												>
													{buttonText}
												</a>
											) : (
												<button className="btn-primary w-full text-center mt-auto" disabled>
													{buttonText}
												</button>
											);
										})()}
									</div>
								))}
							</div>
						</>
					) : service.title === 'Diseño de Logo Empresarial' ? (
						<>
							<p className="text-slate-700 mb-6">{service.details}</p>
							<div className="grid md:grid-cols-3 gap-6 mb-6">
								{planesLogos.map((plan) => (
									<div key={plan.name} className="card p-6 border-l-4 flex flex-col" style={{ borderLeftColor: '#12284C' }}>
										<h3 className="font-bold text-xl text-primary mb-2">{plan.name}</h3>
										<p className="text-sm text-slate-600 mb-3">{plan.desc}</p>
										{plan.price && (
											<div className="mb-4 pb-4 border-b border-slate-200">
												<div className="flex items-baseline gap-2">
													<span className="text-xs text-slate-500">Precio:</span>
													<span className="text-lg font-bold text-primary">{plan.price}</span>
												</div>
											</div>
										)}
										<ul className="space-y-2 mb-6 text-sm text-slate-700 flex-grow">
											{plan.features.map((f, i) => (
												<li key={i} className="flex items-start">
													<span className="text-secondary mr-2">✓</span>
													<span>{f}</span>
												</li>
											))}
										</ul>
										{(() => {
											// Obtener la configuración del link del plan
											let actionConfig = null;
											if (plan.linkKey && formUrls[plan.linkKey]) {
												actionConfig = formUrls[plan.linkKey];
											} else if (plan.link) {
												// Si plan.link es un string (legacy), convertir
												actionConfig = { type: 'form', value: plan.link };
											}
											
											const actionLink = actionConfig ? getActionLink(actionConfig) : null;
											const buttonText = actionConfig ? getActionButtonText(actionConfig) : 'Adquirir Plan';
											
											return actionLink ? (
												<a 
													href={actionLink} 
													target="_blank" 
													rel="noreferrer" 
													className="btn-primary w-full text-center block mt-auto"
												>
													{buttonText}
												</a>
											) : (
												<button className="btn-primary w-full text-center mt-auto" disabled>
													{buttonText}
												</button>
											);
										})()}
									</div>
								))}
							</div>
						</>
					) : (
						<div className="space-y-4">
							<p className="text-slate-700 text-lg leading-relaxed">{service.details}</p>
							{service.features && (
								<div>
									<h3 className="font-semibold text-primary mb-3 text-xl">Incluye:</h3>
									<ul className="list-disc list-inside space-y-2 text-slate-700">
										{service.features.map((f, i) => <li key={i}>{f}</li>)}
									</ul>
								</div>
							)}
							{(() => {
								// Obtener el link del servicio (puede ser directo o desde formUrls)
								let serviceLink = null;
								if (service.link) {
									serviceLink = service.link;
								} else if (service.linkKey && formUrls[service.linkKey]) {
									serviceLink = formUrls[service.linkKey];
								}
								
								return serviceLink ? (
									<a href={serviceLink} target="_blank" rel="noreferrer" className="btn-primary w-full text-center block mt-6">
										Adquirir Plan
									</a>
								) : (
									<button className="btn-primary w-full text-center mt-6" disabled>
										Adquirir Plan
									</button>
								);
							})()}
						</div>
					)}
					
					{/* Sección de Contacto y Redes Sociales */}
					<div className="mt-8 pt-6 border-t border-slate-200">
						<div className="grid md:grid-cols-2 gap-6">
							{/* Información de Contacto */}
							<div>
								<h3 className="font-semibold text-primary mb-4">Contáctanos</h3>
								<div className="space-y-2 text-sm text-slate-700">
									<div className="flex items-center gap-2">
										<span className="font-medium text-slate-600">Teléfono:</span>
										<a href={`tel:+${WHATSAPP_NUMBER}`} className="text-primary hover:underline">
											(849) 607-9129
										</a>
									</div>
									<div className="flex items-center gap-2">
										<span className="font-medium text-slate-600">Email:</span>
										<a href={`mailto:${CONTACT_EMAIL}`} className="text-primary hover:underline">
											{CONTACT_EMAIL}
										</a>
									</div>
									<div className="flex items-center gap-2">
										<span className="font-medium text-slate-600">WhatsApp:</span>
										<a href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noreferrer" className="text-primary hover:underline">
											{WHATSAPP_NUMBER}
										</a>
									</div>
								</div>
							</div>

							{/* Redes Sociales */}
							<div>
								<h3 className="font-semibold text-primary mb-4">Síguenos</h3>
								<div className="flex flex-wrap gap-3">
									<a href={SOCIAL_FACEBOOK} target="_blank" rel="noreferrer" aria-label="Facebook" className="text-slate-600 hover:text-primary transition-colors">
										<FacebookIcon className="w-6 h-6" />
									</a>
									<a href={SOCIAL_INSTAGRAM} target="_blank" rel="noreferrer" aria-label="Instagram" className="text-slate-600 hover:text-primary transition-colors">
										<InstagramIcon className="w-6 h-6" />
									</a>
									<a href={SOCIAL_LINKEDIN} target="_blank" rel="noreferrer" aria-label="LinkedIn" className="text-slate-600 hover:text-primary transition-colors">
										<LinkedinIcon className="w-6 h-6" />
									</a>
									<a href={SOCIAL_TIKTOK} target="_blank" rel="noreferrer" aria-label="TikTok" className="text-slate-600 hover:text-primary transition-colors">
										<TikTokIcon className="w-6 h-6" />
									</a>
									<a href={SOCIAL_THREADS} target="_blank" rel="noreferrer" aria-label="Threads" className="text-slate-600 hover:text-primary transition-colors">
										<ThreadsIcon className="w-6 h-6" />
									</a>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

function FloatingSocialButton() {
	const [isOpen, setIsOpen] = useState(false);

	const socialLinks = [
		{ icon: FacebookIcon, url: SOCIAL_FACEBOOK, label: 'Facebook', color: 'hover:text-blue-600' },
		{ icon: InstagramIcon, url: SOCIAL_INSTAGRAM, label: 'Instagram', color: 'hover:text-pink-600' },
		{ icon: LinkedinIcon, url: SOCIAL_LINKEDIN, label: 'LinkedIn', color: 'hover:text-blue-700' },
		{ icon: TikTokIcon, url: SOCIAL_TIKTOK, label: 'TikTok', color: 'hover:text-black' },
		{ icon: ThreadsIcon, url: SOCIAL_THREADS, label: 'Threads', color: 'hover:text-slate-900' }
	];

	return (
		<div className="fixed top-1/2 -translate-y-1/2 right-6 z-40 flex flex-col items-end gap-3" style={{ pointerEvents: 'none' }}>
			{/* Botones de redes sociales */}
			<div className={`flex flex-col-reverse gap-3 mb-3 transition-all duration-300 ${isOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
				{socialLinks.map((social, index) => (
					<a
						key={social.label}
						href={social.url}
						target="_blank"
						rel="noreferrer"
						aria-label={social.label}
						className={`bg-white rounded-full p-3 shadow-lg hover:shadow-xl transition-all duration-200 border border-slate-300 ${social.color} ${isOpen ? 'scale-100' : 'scale-0'}`}
						style={{ 
							transitionDelay: isOpen ? `${index * 50}ms` : `${(socialLinks.length - index - 1) * 30}ms`,
							pointerEvents: 'auto'
						}}
						onClick={() => setIsOpen(false)}
					>
						<social.icon className="w-5 h-5" />
					</a>
				))}
			</div>

			{/* Botón principal */}
			<button
				onClick={() => setIsOpen(!isOpen)}
				aria-label="Redes sociales"
				aria-expanded={isOpen}
				className={`bg-secondary text-white rounded-full p-4 shadow-lg hover:shadow-xl transition-all duration-300 ${isOpen ? 'bg-primary' : 'hover:bg-secondary/90'}`}
				style={{ pointerEvents: 'auto' }}
			>
				{isOpen ? (
					<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
					</svg>
				) : (
					<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
					</svg>
				)}
			</button>
			
			{/* Botón de WhatsApp - Posicionado relativo al botón de redes sociales */}
			<a
				href={`https://wa.me/${WHATSAPP_NUMBER}`}
				target="_blank"
				rel="noreferrer"
				aria-label="Contactar por WhatsApp"
				className="bg-green-500 text-white rounded-full p-4 shadow-lg hover:shadow-xl hover:bg-green-600 transition-all duration-300 flex items-center justify-center group"
				title="Contactar por WhatsApp"
				style={{ pointerEvents: 'auto' }}
			>
				<svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
					<path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.94L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
				</svg>
			</a>
		</div>
	);
}


function ReferidorForm() {
	const { register, handleSubmit, formState: { errors, isSubmitting }, reset } = useForm();

	async function onSubmit(data) {
		try {
			await axios.post(`${API_URL}/api/notifications/referrer/`, data, { headers: { 'Content-Type': 'application/json' } });
			alert('¡Registro exitoso! Te contactaremos pronto para activar tu cuenta de referidor.');
			reset();
		} catch (e) {
			alert('No fue posible procesar tu registro. Intenta nuevamente.');
		}
	}

	return (
		<div className="max-w-3xl mx-auto">
			<div className="bg-white rounded-xl shadow-lg p-8">
				<div className="mb-8 text-center">
					<h2 className="text-3xl font-bold text-primary mb-3">Regístrate como Referidor</h2>
					<p className="text-slate-600 mb-4">
						Únete a nuestra red de referidores y obtén beneficios al promover nuestros servicios. 
						Completa el formulario y nos pondremos en contacto contigo.
					</p>
					<a 
						href="/inf_referidores.pdf" 
						target="_blank" 
						rel="noopener noreferrer"
						className="inline-flex items-center gap-2 text-primary hover:text-secondary font-medium transition-colors"
					>
						<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
						</svg>
						Descargar información adicional (PDF)
					</a>
				</div>

				<form onSubmit={handleSubmit(onSubmit)} className="grid gap-6">
					<div className="grid sm:grid-cols-2 gap-4">
						<div>
							<label className="block text-sm font-medium text-slate-700 mb-2">Nombre completo *</label>
							<input 
								className="w-full rounded-md border-slate-300 px-4 py-2 focus:ring-primary focus:border-primary" 
								placeholder="Tu nombre completo" 
								{...register('name', { required: 'Este campo es requerido' })} 
							/>
							{errors.name && <p className="text-sm text-red-600 mt-1">{errors.name.message}</p>}
						</div>
						<div>
							<label className="block text-sm font-medium text-slate-700 mb-2">Email *</label>
							<input 
								type="email" 
								className="w-full rounded-md border-slate-300 px-4 py-2 focus:ring-primary focus:border-primary" 
								placeholder="tucorreo@dominio.com" 
								{...register('email', { required: 'Este campo es requerido' })} 
							/>
							{errors.email && <p className="text-sm text-red-600 mt-1">{errors.email.message}</p>}
						</div>
					</div>

					<div className="grid sm:grid-cols-2 gap-4">
						<div>
							<label className="block text-sm font-medium text-slate-700 mb-2">Teléfono *</label>
							<input 
								type="tel" 
								className="w-full rounded-md border-slate-300 px-4 py-2 focus:ring-primary focus:border-primary" 
								placeholder="(809) 123-4567" 
								{...register('phone', { required: 'Este campo es requerido' })} 
							/>
							{errors.phone && <p className="text-sm text-red-600 mt-1">{errors.phone.message}</p>}
						</div>
						<div>
							<label className="block text-sm font-medium text-slate-700 mb-2">WhatsApp</label>
							<input 
								type="tel" 
								className="w-full rounded-md border-slate-300 px-4 py-2 focus:ring-primary focus:border-primary" 
								placeholder="(809) 123-4567" 
								{...register('whatsapp')} 
							/>
						</div>
					</div>

					<div>
						<label className="block text-sm font-medium text-slate-700 mb-2">¿Cómo conociste nuestros servicios? *</label>
						<select 
							className="w-full rounded-md border-slate-300 px-4 py-2 focus:ring-primary focus:border-primary" 
							{...register('how_known', { required: 'Este campo es requerido' })}
						>
							<option value="">Selecciona una opción</option>
							<option value="redes_sociales">Redes Sociales</option>
							<option value="referido">Referido por un cliente</option>
							<option value="buscador">Buscador en internet</option>
							<option value="evento">Evento o conferencia</option>
							<option value="otro">Otro</option>
						</select>
						{errors.how_known && <p className="text-sm text-red-600 mt-1">{errors.how_known.message}</p>}
					</div>

					<div>
						<label className="block text-sm font-medium text-slate-700 mb-2">¿Qué servicios te interesa promover? *</label>
						<div className="grid sm:grid-cols-2 gap-3 mt-2">
							{['Constitución de Empresas', 'Contabilidad y Fiscal', 'Asesoría Legal', 'Diseño Gráfico', 'Otros Servicios'].map((service) => (
								<label key={service} className="flex items-center gap-2 cursor-pointer">
									<input 
										type="checkbox" 
										value={service} 
										className="rounded border-slate-300 text-primary focus:ring-primary" 
										{...register('services_interested')} 
									/>
									<span className="text-sm text-slate-700">{service}</span>
								</label>
							))}
						</div>
					</div>

					<div>
						<label className="block text-sm font-medium text-slate-700 mb-2">Cuéntanos sobre ti *</label>
						<textarea 
							rows={4} 
							className="w-full rounded-md border-slate-300 px-4 py-2 focus:ring-primary focus:border-primary" 
							placeholder="Describe brevemente tu experiencia, red de contactos o cómo planeas promover nuestros servicios..." 
							{...register('description', { required: 'Este campo es requerido' })} 
						/>
						{errors.description && <p className="text-sm text-red-600 mt-1">{errors.description.message}</p>}
					</div>

					<div>
						<label className="flex items-center gap-2 cursor-pointer">
							<input 
								type="checkbox" 
								className="rounded border-slate-300 text-primary focus:ring-primary" 
								{...register('accept_terms', { required: 'Debes aceptar los términos' })} 
							/>
							<span className="text-sm text-slate-700">
								Acepto los <Link to="/terminos" className="text-primary hover:underline">términos y condiciones</Link> y la <Link to="/privacidad" className="text-primary hover:underline">política de privacidad</Link> *
							</span>
						</label>
						{errors.accept_terms && <p className="text-sm text-red-600 mt-1">{errors.accept_terms.message}</p>}
					</div>

					<div className="flex gap-3">
						<button 
							disabled={isSubmitting} 
							type="submit" 
							className="btn-primary flex-1"
						>
							{isSubmitting ? 'Enviando...' : 'Registrarse como Referidor'}
						</button>
					</div>
				</form>
			</div>
		</div>
	);
}

function ContactForm() {
	const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm();

	async function onSubmit(data) {
		try {
			await axios.post(`${API_URL}/api/notifications/contact/`, data, { headers: { 'Content-Type': 'application/json' } });
			alert('Mensaje enviado. Te contactaremos pronto.');
		} catch (e) {
			alert('No fue posible enviar el mensaje. Intenta nuevamente.');
		}
	}

	return (
		<form onSubmit={handleSubmit(onSubmit)} className="grid gap-4">
			<div>
				<label className="block text-sm font-medium text-slate-700">Nombre</label>
				<input className="mt-1 w-full rounded-md border-slate-300" placeholder="Tu nombre" {...register('name', { required: true })} />
				{errors.name && <p className="text-sm text-red-600 mt-1">Requerido</p>}
			</div>
			<div className="grid sm:grid-cols-2 gap-4">
				<div>
					<label className="block text-sm font-medium text-slate-700">Email</label>
					<input type="email" className="mt-1 w-full rounded-md border-slate-300" placeholder="tucorreo@dominio.com" {...register('email', { required: true })} />
					{errors.email && <p className="text-sm text-red-600 mt-1">Requerido</p>}
				</div>
				<div>
					<label className="block text-sm font-medium text-slate-700">Teléfono</label>
					<input className="mt-1 w-full rounded-md border-slate-300" placeholder="Opcional" {...register('phone')} />
				</div>
			</div>
			<div>
				<label className="block text-sm font-medium text-slate-700">Mensaje</label>
				<textarea rows={4} className="mt-1 w-full rounded-md border-slate-300" placeholder="Cuéntanos brevemente" {...register('message', { required: true })} />
				{errors.message && <p className="text-sm text-red-600 mt-1">Requerido</p>}
			</div>
			<div className="flex gap-3">
				<button disabled={isSubmitting} type="submit" className="px-5 py-3 rounded-md bg-primary text-white font-semibold">Enviar</button>
					<a href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noreferrer" className="px-5 py-3 rounded-md border border-slate-300">WhatsApp</a>
			</div>
				<p className="text-xs text-slate-500">Al enviar, aceptas nuestra <Link to="/privacidad" className="underline">Política de privacidad</Link>.</p>
		</form>
	);
}

function LegalPage({ title, children }) {
	return (
		<div className="container-width py-16">
			<h1 className="text-3xl font-bold text-primary mb-4">{title}</h1>
			<div className="prose max-w-none">{children}</div>
		</div>
	);
}

function Privacidad() {
	return (
		<LegalPage title="Política de privacidad">
			<p>Este sitio recopila los datos mínimos necesarios para atender tus consultas. No compartimos tu información con terceros salvo obligación legal. Puedes solicitar acceso o eliminación escribiendo a <a href="mailto:info@psasesoresconsultores.com">info@psasesoresconsultores.com</a>.</p>
		</LegalPage>
	);
}

function Terminos() {
	return (
		<LegalPage title="Términos y condiciones">
			<p>El uso del sitio implica la aceptación de estos términos. El contenido se ofrece con fines informativos y puede cambiar sin previo aviso.</p>
		</LegalPage>
	);
}

function Cookies() {
	return (
		<LegalPage title="Política de cookies">
			<p>Utilizamos cookies técnicas esenciales para el funcionamiento del sitio. No empleamos analítica ni cookies de terceros por el momento.</p>
		</LegalPage>
	);
}

function ReferidoresPage() {
	return (
		<div className="min-h-screen bg-slate-50 py-16">
			<div className="container-width">
				<ReferidorForm />
			</div>
		</div>
	);
}

function TestimonialsSection() {
	const { data: testimonials = [], isLoading, error } = useQuery({
		queryKey: ['testimonials'],
		queryFn: async () => {
			try {
				return await contentApi.getTestimonials();
			} catch (err) {
				console.error('❌ Error cargando testimonials:', err);
				// Retornar array vacío en caso de error para que no se quede cargando
				return [];
			}
		},
		staleTime: 5 * 60 * 1000,
		retry: 1,
	});

	const defaultTestimonials = [
		{ quote: 'Nos ayudaron a constituir la empresa y cumplir a tiempo. 10/10.', name: 'Cliente PyME', company: '' },
		{ quote: 'El outsourcing contable nos liberó tiempo para crecer.', name: 'Emprendimiento Retail', company: '' },
		{ quote: 'Auditoría precisa y recomendaciones accionables.', name: 'ONG Local', company: '' }
	];

	const displayTestimonials = testimonials.length > 0 ? testimonials : defaultTestimonials;
	const [currentIndex, setCurrentIndex] = useState(0);
	const [paused, setPaused] = useState(false);
	const [cardsPerView, setCardsPerView] = useState(3); // Número de cards visibles simultáneamente

	// Responsive: ajustar cardsPerView según el ancho de pantalla
	useEffect(() => {
		const handleResize = () => {
			if (window.innerWidth < 768) {
				setCardsPerView(1); // Móvil: 1 card
			} else if (window.innerWidth < 1024) {
				setCardsPerView(2); // Tablet: 2 cards
			} else {
				setCardsPerView(3); // Desktop: 3 cards
			}
			setCurrentIndex(0); // Resetear al cambiar el tamaño
		};

		handleResize(); // Ejecutar al montar
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	// Función para generar iniciales del nombre
	const getInitials = (name) => {
		if (!name) return 'CS';
		const parts = name.trim().split(' ');
		if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
		return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
	};

	// Función para generar color de avatar basado en el nombre
	const getAvatarColor = (name) => {
		if (!name) return 'bg-primary';
		const colors = [
			'bg-primary',
			'bg-secondary',
			'bg-blue-600',
			'bg-purple-600',
			'bg-pink-600',
			'bg-orange-600',
			'bg-teal-600',
			'bg-indigo-600'
		];
		const index = name.length % colors.length;
		return colors[index];
	};

	// Calcular índice máximo: permite mostrar cardsPerView cards desde cualquier posición
	// Ejemplo: si hay 10 testimonios y cardsPerView=3, maxIndex = 10 - 3 = 7
	// Esto permite mostrar testimonios [7, 8, 9] como última posición
	const maxIndex = displayTestimonials.length > cardsPerView ? displayTestimonials.length - cardsPerView : 0;

	// Auto-rotación de testimonios - avanza de 1 en 1
	useEffect(() => {
		if (!displayTestimonials || displayTestimonials.length <= cardsPerView || paused) return;
		
		const interval = setInterval(() => {
			setCurrentIndex((prev) => {
				// Avanzar de 1 en 1
				const nextIndex = prev + 1;
				// Si excede el máximo, volver al inicio
				if (nextIndex > maxIndex) {
					return 0;
				}
				return nextIndex;
			});
		}, 5000); // Cambia cada 5 segundos

		return () => clearInterval(interval);
	}, [displayTestimonials, paused, maxIndex]);

	const goNext = () => {
		const nextIndex = currentIndex + 1;
		setCurrentIndex(nextIndex > maxIndex ? 0 : nextIndex);
	};

	const goPrev = () => {
		const prevIndex = currentIndex - 1;
		setCurrentIndex(prevIndex < 0 ? maxIndex : prevIndex);
	};

	return (
		<Section id="casos" title="Casos y testimonios">
			{isLoading && testimonials.length === 0 ? (
				<div className="flex gap-6 overflow-hidden">
					{[1, 2, 3].map(i => (
						<div key={i} className="card p-5 animate-pulse flex-shrink-0" style={{ width: 'calc(33.333% - 1rem)' }}>
							<div className="flex gap-3 mb-3">
								<div className="w-12 h-12 bg-slate-200 rounded-full"></div>
								<div className="flex-1">
									<div className="h-4 bg-slate-200 rounded w-24 mb-2"></div>
									<div className="h-3 bg-slate-200 rounded w-16"></div>
								</div>
							</div>
							<div className="h-20 bg-slate-200 rounded"></div>
						</div>
					))}
				</div>
			) : (
				<div 
					className="relative px-12"
					onMouseEnter={() => setPaused(true)}
					onMouseLeave={() => setPaused(false)}
				>
					{/* Controles de navegación - Fuera del área visible */}
					{displayTestimonials.length > cardsPerView && (
						<>
							<button 
								onClick={goPrev}
								aria-label="Anterior"
								className="absolute left-0 top-1/2 -translate-y-1/2 bg-white rounded-full p-3 shadow-lg hover:shadow-xl transition-all z-10 hover:scale-110"
							>
								<svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
								</svg>
							</button>
							<button 
								onClick={goNext}
								aria-label="Siguiente"
								className="absolute right-0 top-1/2 -translate-y-1/2 bg-white rounded-full p-3 shadow-lg hover:shadow-xl transition-all z-10 hover:scale-110"
							>
								<svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
								</svg>
							</button>
						</>
					)}

					{/* Contenedor del carrusel */}
					<div className="overflow-hidden w-full relative">
						<div 
							className="flex gap-6 transition-transform duration-500 ease-in-out"
							style={{ 
								// El transform mueve por el ancho de una card + gap
								// Cada card ocupa: (100% - gaps) / cardsPerView del visible
								// Cada gap es 1.5rem (gap-6)
								// Transform mueve por: cardWidth + gap = (100% - gaps) / cardsPerView + 1.5rem
								transform: `translateX(calc(-${currentIndex} * ((100% - ${(cardsPerView - 1) * 1.5}rem) / ${cardsPerView} + 1.5rem)))`
							}}
						>
							{displayTestimonials.map((t, i) => {
								// Cálculo del ancho de cada card
								// Cada card debe tener el mismo ancho: (100% del visible - gaps) / cardsPerView
								const gapCount = cardsPerView - 1;
								const cardWidth = `calc((100% - ${gapCount * 1.5}rem) / ${cardsPerView})`;
								return (
								<div 
									key={t.id || i} 
									className="card p-5 hover:shadow-lg transition-shadow"
									style={{ 
										width: cardWidth,
										minWidth: cardWidth,
										maxWidth: cardWidth,
										flexShrink: 0,
										flexGrow: 0,
										boxSizing: 'border-box'
									}}
								>
									{/* Header tipo redes sociales */}
									<div className="flex items-start gap-3 mb-4">
										{/* Avatar circular */}
										<div className={`${getAvatarColor(t.name)} rounded-full w-12 h-12 flex items-center justify-center text-white font-bold text-sm flex-shrink-0`}>
											{getInitials(t.name)}
										</div>
										{/* Nombre y verificación */}
										<div className="flex-1 min-w-0">
											<div className="flex items-center gap-2">
												<h4 className="font-semibold text-slate-900 text-sm">{t.name}</h4>
												<svg className="w-4 h-4 text-blue-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
													<path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
												</svg>
											</div>
											{t.company ? (
												<p className="text-xs text-slate-500">{t.company}</p>
											) : (
												<p className="text-xs text-slate-500">Cliente verificado</p>
											)}
										</div>
										{/* Menú de opciones (tres puntos) */}
										<button className="text-slate-400 hover:text-slate-600" aria-label="Más opciones">
											<svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
												<path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
											</svg>
										</button>
									</div>

									{/* Contenido del comentario */}
									<div className="mb-3">
										<p className="text-slate-800 leading-relaxed text-sm whitespace-pre-wrap">{t.quote}</p>
									</div>

									{/* Calificación con estrellas */}
									<div className="mb-3 flex items-center gap-1">
										{[1, 2, 3, 4, 5].map((star) => (
											<svg key={star} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
												<path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
											</svg>
										))}
									</div>

									{/* Footer tipo redes sociales */}
									<div className="flex items-center justify-between pt-3 border-t border-slate-100">
										<div className="flex items-center gap-4 text-red-500">
											<button className="hover:text-red-600 transition-colors" aria-label="Me gusta">
												<svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
													<path d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
												</svg>
											</button>
											<span className="text-xs text-red-500">Me gusta</span>
										</div>
										<span className="text-xs text-slate-400">Hace 1 semana</span>
									</div>
								</div>
								);
							})}
						</div>
					</div>

					{/* Indicadores de posición */}
					{displayTestimonials.length > cardsPerView && maxIndex > 0 && (
						<div className="flex justify-center gap-2 mt-6">
							{Array.from({ length: maxIndex + 1 }).map((_, i) => {
								const isActive = currentIndex === i;
								return (
									<button
										key={i}
										onClick={() => setCurrentIndex(i)}
										aria-label={`Ir a posición ${i + 1}`}
										className={`h-2 rounded-full transition-all ${
											isActive 
												? 'w-8 bg-primary' 
												: 'w-2 bg-slate-300 hover:bg-slate-400'
										}`}
									/>
								);
							})}
						</div>
					)}
				</div>
			)}
		</Section>
	);
}

function PartnersSection() {
	const { data: partners = [], isLoading, error } = useQuery({
		queryKey: ['partners'],
		queryFn: async () => {
			try {
				return await contentApi.getPartners();
			} catch (err) {
				console.error('❌ Error cargando partners:', err);
				// Retornar array vacío en caso de error para que no se quede cargando
				return [];
			}
		},
		staleTime: 5 * 60 * 1000,
		retry: 1,
	});

	return (
		<Section id="partners" title="Partners">
			{isLoading && partners.length === 0 ? (
				<div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-6">
					{[1, 2, 3, 4, 5, 6].map(i => (
						<div key={i} className="card p-2 flex items-center justify-center animate-pulse min-h-[120px]">
							<div className="w-[95%] h-[95%] bg-slate-200 rounded"></div>
						</div>
					))}
				</div>
			) : partners.length > 0 ? (
				<div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-6">
					{partners.map((p) => (
						<div key={p.id} className="card p-2 flex items-center justify-center hover:shadow-lg transition-all duration-300 hover:scale-105 min-h-[120px]">
							{p.logo_url ? (
								p.website ? (
									<a 
										href={p.website} 
										target="_blank" 
										rel="noreferrer" 
										className="block w-full h-full flex items-center justify-center"
										aria-label={p.name}
									>
										<img 
											src={p.logo_url} 
											alt={p.name} 
											className="w-full h-full max-w-[95%] max-h-[95%] object-contain filter grayscale hover:grayscale-0 transition-all duration-300"
										/>
									</a>
								) : (
									<img 
										src={p.logo_url} 
										alt={p.name} 
										className="w-full h-full max-w-[95%] max-h-[95%] object-contain filter grayscale"
									/>
								)
							) : (
								<span className="text-slate-400 text-sm text-center">{p.name}</span>
							)}
						</div>
					))}
				</div>
			) : null}
		</Section>
	);
}

function FAQSection() {
	const [openCategories, setOpenCategories] = useState(new Set());
	const [openQuestions, setOpenQuestions] = useState(new Set());

	const faqs = [
		{
			category: 'Constitución de Empresas & ONG',
			questions: [
				{
					q: '¿Por qué debo formalizar mi negocio?',
					a: 'Formalizarse te permite operar legalmente, acceder a contratos con empresas e instituciones, emitir comprobantes fiscales, proteger tu marca y proyectar confianza ante tus clientes. Además, te abre las puertas al crédito y a programas de apoyo empresarial.'
				},
				{
					q: '¿Qué necesito para constituir mi empresa?',
					a: 'Solo necesitas tu cédula o pasaporte vigente, completar nuestro formulario con las informaciones del propietario o los socios (si aplica), informaciones de la empresa y subir la cédula de ambos lados. Luego de que nos apruebe el formulario revisado y haya realizado el avance de uno de nuestro plan elegido, nuestro equipo legal dará inicio a su proceso constitutivo.'
				},
				{
					q: '¿Cuánto tiempo tarda la constitución de una empresa?',
					a: 'El proceso puede tomar entre 15 y 25 días laborables, dependiendo del tipo de empresa, los tiempos de las instituciones que no presente retrasos y que nos provea las informaciones a tiempo. Nosotros te mantenemos informado paso a paso hasta la entrega final de todos los documentos constitutivos.'
				},
				{
					q: '¿Cuál es el costo de formalizar mi negocio?',
					a: 'Los costos varían según el tipo de sociedad (EIRL, SRL, ONG, etc.) y los servicios incluidos según tu necesidad. Ofrecemos paquetes asequibles diseñados especialmente para emprendedores que desean iniciar con bases firmes.'
				},
				{
					q: '¿Qué tipo de empresa me conviene crear: EIRL, SRL?',
					a: 'Depende si cuenta con socio o eres único dueño. Empresa SRL significa Sociedad de Responsabilidad Limitada, se conforma con un mínimo de 2 socios y un máximo de 50. Esta Sociedad a diferencia de la EIRL le brinda mayor protección a su patrimonio, ya que su compromiso llega hasta el aporte realizado en la empresa. La Empresa Individual de Responsabilidad Limitada (EIRL) es la más utilizada por empresarios que buscan iniciar un negocio y tomar todas las decisiones individualmente, sin socios. Es un tipo de organización en el que cualquier persona natural puede adquirir personalidad jurídica. La razón de una EIRL es siempre comercial y cuenta con un patrimonio propio, distinto al del titular.'
				},
				{
					q: '¿Puedo iniciar mi empresa y recibir asesoría, aunque aún no tenga ingresos?',
					a: 'Sí. Muchos de nuestros clientes inician con nosotros desde la idea de negocio. Te guiamos en la planificación, constitución y organización contable, incluso antes de empezar a facturar.'
				},
				{
					q: '¿Ofrecen planes de pago para la constitución?',
					a: 'Sí, ofrecemos planes flexibles con opción de pago de avance. Por ejemplo, en nuestros planes para fundaciones y ONG puedes pagar un avance del 50% al inicio y el resto al finalizar el proceso. Consulta nuestros planes para más detalles.'
				},
				{
					q: '¿Qué incluye el registro de una ONG o fundación?',
					a: 'Incluye: acta de asamblea general constitutiva, estatutos, certificación de incorporación por el Ministerio Público, resolución de incorporación, certificaciones del Ayuntamiento, Juzgado de Paz y Cámara Civil, y registro en el RNC. Los planes superiores incluyen acceso a oficina virtual de DGII, comprobantes fiscales, registro TSS e identidad visual.'
				}
			]
		},
		{
			category: 'Outsourcing Contable y Fiscal',
			questions: [
				{
					q: '¿Qué incluye un plan de igualas contables?',
					a: 'Los planes varían según tus necesidades. El plan básico incluye declaraciones esenciales (IR-3, formatos 606, 607, 608, IT-1). Los planes superiores incluyen asesoría financiera y fiscal, gestión de TSS e INFOTEP, elaboración de nómina, declaración IR-17, anticipos de ISR, registro contable completo, y elaboración de estados financieros.'
				},
				{
					q: '¿Puedo cambiar de plan en cualquier momento?',
					a: 'Sí, nuestros planes son flexibles y puedes actualizar o cambiar tu plan según las necesidades de tu empresa crezcan. Te ayudamos a evaluar qué plan es el más adecuado para tu situación actual.'
				},
				{
					q: '¿Qué beneficios tiene contratar la Iguala Contable desde el inicio?',
					a: 'Te libera del estrés de llevar cuentas, impuestos y reportes fiscales. Con nuestra Iguala Contable, te aseguramos que tu contabilidad esté siempre al día y evites sanciones. Así puedes concentrarte en hacer crecer tu negocio.'
				},
				{
					q: '¿Qué es el outsourcing contable y por qué lo necesito?',
					a: 'El outsourcing contable te permite delegar toda la gestión contable, fiscal y administrativa a profesionales especializados, liberando tu tiempo para enfocarte en el crecimiento de tu negocio. Incluye desde declaraciones fiscales hasta elaboración de estados financieros, según el plan que elijas.'
				},
				{
					q: '¿Cómo garantizan la confidencialidad de mi información?',
					a: 'Manejamos toda la información con estrictos protocolos de confidencialidad y seguridad. Utilizamos sistemas seguros para almacenar y procesar la información de nuestros clientes, cumpliendo con todas las normativas de protección de datos.'
				},
				{
					q: '¿Ofrecen asesoría fiscal personalizada?',
					a: 'Sí, nuestros planes PYME, PRO y PLUS incluyen asesoría fiscal personalizada. Te ayudamos a optimizar tu situación fiscal, cumplir con todas las obligaciones, y tomar decisiones informadas sobre tu empresa.'
				}
			]
		},
		{
			category: 'Procesos y Tiempos',
			questions: [
				{
					q: '¿Cómo puedo empezar con ustedes?',
					a: 'Solo tienes que contactarnos al +1 (849) 607-9129 o escribirnos por WhatsApp. De ser necesario agendamos una breve reunión inicial donde analizamos tu caso y te presentamos la mejor propuesta para formalizar tu empresa y llevar tus finanzas al día.'
				},
				{
					q: '¿Puedo recibir atención virtual si no estoy en Santo Domingo?',
					a: 'Por supuesto. Atendemos clientes en toda la República Dominicana. Podemos realizar el proceso completo de forma virtual y segura, con seguimiento personalizado vía WhatsApp, correo o videollamada.'
				},
				{
					q: '¿Qué métodos de pago aceptan?',
					a: 'Aceptamos transferencias bancarias, depósitos y otros métodos de pago convencionales. Puedes contactarnos para conocer las opciones disponibles y elegir la que mejor se adapte a ti.'
				},
				{
					q: '¿Ofrecen servicios de consultoría continua?',
					a: 'Sí, nuestros planes de outsourcing incluyen consultoría continua. Además, ofrecemos servicios de consultoría independiente para temas específicos como financiamiento, estructura empresarial, y optimización de procesos.'
				}
			]
		},
		{
			category: 'Servicios Adicionales',
			questions: [
				{
					q: '¿Ofrecen servicios de manejo de redes sociales?',
					a: 'Sí, ofrecemos planes de manejo de redes sociales que incluyen estrategia de contenido, publicación regular, análisis de rendimiento, campañas publicitarias, y optimización de perfiles. Ideal para empresas que quieren mejorar su presencia digital.'
				},
				{
					q: '¿Realizan auditorías?',
					a: 'Sí, ofrecemos servicios de auditoría para evaluar y mejorar los procesos contables, fiscales y administrativos de tu empresa. Nuestras auditorías incluyen recomendaciones accionables para optimizar tu operación.'
				},
				{
					q: '¿Ayudan con la renovación del Registro Mercantil?',
					a: 'Sí, ofrecemos el servicio de renovación del Registro Mercantil. Nos encargamos de todo el proceso para que tu empresa mantenga su registro vigente sin interrupciones.'
				},
				{
					q: '¿Ustedes me ayudan también con el logo, sello y la imagen corporativa?',
					a: 'Sí. En Paulino Sosa Asesores & Consultores te ofrecemos un paquete integral que incluye la constitución legal, diseño de logotipo, creación del sello y asesoría en identidad de marca para que inicies con una imagen profesional.'
				},
				{
					q: '¿Ofrecen servicios de diseño de identidad visual?',
					a: 'Sí, como servicio complementario ofrecemos diseño de logos simples y profesionales, así como paquetes completos de identidad visual para empresas y ONG que necesitan desarrollar su marca.'
				}
			]
		}
	];

	const toggleCategory = (catIndex) => {
		const newOpenCategories = new Set(openCategories);
		if (newOpenCategories.has(catIndex)) {
			newOpenCategories.delete(catIndex);
			// Cerrar todas las preguntas de esta categoría cuando se cierra
			const newOpenQuestions = new Set(openQuestions);
			faqs[catIndex].questions.forEach((_, qIndex) => {
				newOpenQuestions.delete(`${catIndex}-${qIndex}`);
			});
			setOpenQuestions(newOpenQuestions);
		} else {
			newOpenCategories.add(catIndex);
		}
		setOpenCategories(newOpenCategories);
	};

	const toggleQuestion = (catIndex, questionIndex) => {
		const key = `${catIndex}-${questionIndex}`;
		const newOpenQuestions = new Set(openQuestions);
		if (newOpenQuestions.has(key)) {
			newOpenQuestions.delete(key);
		} else {
			newOpenQuestions.add(key);
		}
		setOpenQuestions(newOpenQuestions);
	};

	const expandAll = () => {
		const allCategories = new Set(faqs.map((_, idx) => idx));
		setOpenCategories(allCategories);
		const allQuestions = new Set();
		faqs.forEach((category, catIndex) => {
			category.questions.forEach((_, qIndex) => {
				allQuestions.add(`${catIndex}-${qIndex}`);
			});
		});
		setOpenQuestions(allQuestions);
	};

	const collapseAll = () => {
		setOpenCategories(new Set());
		setOpenQuestions(new Set());
	};

	return (
		<Section id="faq" title="Preguntas frecuentes">
			<div className="max-w-4xl mx-auto">
				{/* Controles globales */}
				<div className="flex justify-end gap-3 mb-6">
					<button
						onClick={expandAll}
						className="text-sm px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors"
					>
						Expandir todo
					</button>
					<button
						onClick={collapseAll}
						className="text-sm px-4 py-2 bg-slate-200 text-slate-700 rounded-md hover:bg-slate-300 transition-colors"
					>
						Colapsar todo
					</button>
				</div>

				<div className="space-y-4">
					{faqs.map((category, catIndex) => {
						const isCategoryOpen = openCategories.has(catIndex);
						const questionCount = category.questions.length;
						
						return (
							<div key={catIndex} className="card border-l-4 overflow-hidden" style={{ borderLeftColor: '#12284C' }}>
								{/* Header de categoría */}
								<button
									onClick={() => toggleCategory(catIndex)}
									className="w-full text-left p-5 flex items-center justify-between hover:bg-slate-50 transition-colors"
									aria-expanded={isCategoryOpen}
								>
									<div className="flex items-center gap-3">
										<h3 className="text-xl font-bold text-primary">{category.category}</h3>
										<span className="text-sm text-slate-500 bg-slate-100 px-2 py-1 rounded-full">
											{questionCount} {questionCount === 1 ? 'pregunta' : 'preguntas'}
										</span>
									</div>
									<svg
										className={`w-6 h-6 text-secondary flex-shrink-0 transition-transform ${isCategoryOpen ? 'rotate-180' : ''}`}
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
									</svg>
								</button>

								{/* Preguntas dentro de la categoría */}
								{isCategoryOpen && (
									<div className="border-t border-slate-200">
										<div className="space-y-2 p-4">
											{category.questions.map((faq, qIndex) => {
												const questionKey = `${catIndex}-${qIndex}`;
												const isQuestionOpen = openQuestions.has(questionKey);
												
												return (
													<div key={qIndex} className="bg-slate-50 rounded-lg border border-slate-200">
														<button
															onClick={() => toggleQuestion(catIndex, qIndex)}
															className="w-full text-left p-4 flex items-center justify-between hover:bg-slate-100 transition-colors rounded-lg"
															aria-expanded={isQuestionOpen}
														>
															<span className="font-semibold text-slate-900 pr-4 flex-1">{faq.q}</span>
															<svg
																className={`w-5 h-5 text-secondary flex-shrink-0 transition-transform ${isQuestionOpen ? 'rotate-180' : ''}`}
																fill="none"
																stroke="currentColor"
																viewBox="0 0 24 24"
															>
																<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
															</svg>
														</button>
														{isQuestionOpen && (
															<div className="px-4 pb-4 pt-2">
																<p className="text-slate-700 leading-relaxed">{faq.a}</p>
															</div>
														)}
													</div>
												);
											})}
										</div>
									</div>
								)}
							</div>
						);
					})}
				</div>
			</div>
		</Section>
	);
}

function NationalCoverageSection() {
	const { data: statsData = {}, isLoading } = useQuery({
		queryKey: ['project-statistics'],
		queryFn: contentApi.getProjectStatistics,
		staleTime: 5 * 60 * 1000,
		retry: 1,
	});

	const [currentPointIndex, setCurrentPointIndex] = useState(0);
	const [isVisible, setIsVisible] = useState(true);
	const [regionPaths, setRegionPaths] = useState({});

	// Hook para animar contador
	const useAnimatedCounter = (targetValue, duration = 5000) => {
		const [count, setCount] = useState(0);
		const [hasAnimated, setHasAnimated] = useState(false);

		useEffect(() => {
			if (!hasAnimated && targetValue !== undefined && targetValue !== null) {
				const numericValue = parseInt(targetValue.toString().replace(/\D/g, '')) || 0;
				const startTime = Date.now();
				const startValue = 0;
				let animationFrameId;
				let pauseTimeout;
				let animationComplete = false;

				const animate = () => {
					const elapsed = Date.now() - startTime;
					const progress = Math.min(elapsed / duration, 1);
					
					if (progress < 1) {
						// Fase de animación - continuar animando
						const easeOut = 1 - Math.pow(1 - progress, 4);
						const currentValue = Math.floor(startValue + (numericValue - startValue) * easeOut);
						
						// Si está muy cerca del final (más del 97% del progreso), mostrar directamente el valor final
						// Esto asegura que llegue al valor final, no a uno anterior
						if (progress >= 0.97 || currentValue >= numericValue - 1) {
							setCount(numericValue);
						} else {
							setCount(currentValue);
						}
						
						animationFrameId = requestAnimationFrame(animate);
					} else {
						// La animación ha terminado - asegurar que muestre el valor final
						setCount(numericValue);
						
						// Solo ejecutar la pausa una vez
						if (!animationComplete) {
							animationComplete = true;
							
							// Pausa de 1500ms DESPUÉS de llegar al valor final
							// El valor final se mantiene visible durante este tiempo
							pauseTimeout = setTimeout(() => {
								setHasAnimated(true);
							}, 5000);
						}
					}
				};

				animationFrameId = requestAnimationFrame(animate);

				return () => {
					if (animationFrameId) {
						cancelAnimationFrame(animationFrameId);
					}
					if (pauseTimeout) {
						clearTimeout(pauseTimeout);
					}
				};
			}
		}, [targetValue, duration, hasAnimated]);

		return count;
	};

	// Puntos de cobertura estratégicos en República Dominicana
	// Coordenadas basadas en los centros (cx, cy) del state_bbox_array del mapdata.js
	// ViewBox: 1000 x 686
	const coveragePoints = [
		// Región Norte - usando centros de las regiones Cibao
		{ 
			x: 380, y: 128, 
			name: 'Cibao Norte',
			provinces: ['Santiago', 'Espaillat', 'Puerto Plata']
		},
		{ 
			x: 188, y: 127, 
			name: 'Cibao Noroeste',
			provinces: ['Monte Cristi', 'Dajabón', 'Santiago Rodríguez', 'Valverde']
		},
		{ 
			x: 528, y: 187, 
			name: 'Cibao Nordeste',
			provinces: ['Duarte', 'María Trinidad Sánchez', 'Samaná', 'Hermanas Mirabal']
		},
		
		// Región Central
		{ 
			x: 427, y: 276, 
			name: 'Cibao Sur',
			provinces: ['La Vega', 'Monseñor Nouel', 'Sánchez Ramírez']
		},
		{ 
			x: 201, y: 300, 
			name: 'El Valle',
			provinces: ['San Juan', 'Elías Piña']
		},
		
		// Región Este
		{ 
			x: 653, y: 318, 
			name: 'Higuamo',
			provinces: ['San Pedro de Macorís', 'Hato Mayor', 'Monte Plata']
		},
		{ 
			x: 833, y: 365, 
			name: 'Yuma',
			provinces: ['La Altagracia', 'La Romana', 'El Seibo']
		},
		
		// Región Sur (Santo Domingo y alrededores)
		{ 
			x: 560, y: 386, 
			name: 'Ozama',
			provinces: ['Distrito Nacional', 'Santo Domingo', 'Santo Domingo Norte', 'Santo Domingo Este']
		},
		{ 
			x: 448, y: 410, 
			name: 'Valdesia',
			provinces: ['San Cristóbal', 'Peravia', 'San José de Ocoa']
		},
		
		// Región Suroeste
		{ 
			x: 192, y: 483, 
			name: 'Enriquillo',
			provinces: ['Barahona', 'Independencia', 'Pedernales', 'Baoruco', 'Azua']
		},
	];

	// Cargar paths del SVG
	useEffect(() => {
		fetch('/dominican-republic.svg')
			.then(res => res.text())
			.then(svgText => {
				const parser = new DOMParser();
				const svgDoc = parser.parseFromString(svgText, 'image/svg+xml');
				const paths = {};
				// Mapear nombres de regiones a IDs del SVG
				const regionIdMap = {
					'Cibao Norte': 'DO35',
					'Cibao Noroeste': 'DO34',
					'Cibao Nordeste': 'DO33',
					'Cibao Sur': 'DO36',
					'El Valle': 'DO37',
					'Higuamo': 'DO39',
					'Yuma': 'DO42',
					'Ozama': 'DO40',
					'Valdesia': 'DO41',
					'Enriquillo': 'DO38'
				};
				
				Object.entries(regionIdMap).forEach(([name, id]) => {
					const pathElement = svgDoc.getElementById(id) || svgDoc.querySelector(`[id="${id}"]`);
					if (pathElement) {
						paths[name] = pathElement.getAttribute('d') || pathElement.outerHTML;
					}
				});
				
				setRegionPaths(paths);
			})
			.catch(err => console.error('Error cargando SVG:', err));
	}, []);

	// Animación del marcador: aparece y desaparece en diferentes puntos
	useEffect(() => {
		const interval = setInterval(() => {
			// Fade out
			setIsVisible(false);
			
			setTimeout(() => {
				// Cambiar a siguiente punto
				setCurrentPointIndex((prev) => (prev + 1) % coveragePoints.length);
				// Fade in
				setIsVisible(true);
			}, 300); // Tiempo de transición
		}, 2000); // Duración en cada punto

		return () => clearInterval(interval);
	}, [coveragePoints.length]);

	const categoryLabels = {
		constitucion: 'Constitución de Empresas & ONG',
		outsourcing: 'Outsourcing Contable y Fiscal',
		otros: 'Otros Servicios'
	};

	const categories = ['constitucion', 'outsourcing', 'otros'];
	const hasStats = categories.some(cat => statsData[cat] && Array.isArray(statsData[cat]) && statsData[cat].length > 0);

	// Componente de tarjeta con contador animado
	const StatCardWithAnimation = ({ stat, category }) => {
		const animatedCount = useAnimatedCounter(stat.value, 1500);
		
		// Formatear el número con separadores de miles si es necesario
		const formatNumber = (num) => {
			return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
		};

		return (
			<div 
				className="card p-4 border-l-4 hover:shadow-lg transition-all duration-300 hover:scale-105"
				style={{ borderLeftColor: '#12284C' }}
			>
				<div className="flex items-baseline gap-1 mb-1">
					{stat.suffix && (
						<span className="text-xl sm:text-2xl text-secondary font-bold mr-1">
							{stat.suffix}
						</span>
					)}
					<span className="text-2xl sm:text-3xl font-bold text-primary tabular-nums">
						{formatNumber(animatedCount)}
					</span>
				</div>
				<p className="text-xs sm:text-sm text-slate-600 font-medium leading-tight">
					{stat.title}
				</p>
				<p className="text-xs text-slate-400 mt-1 hidden sm:block">
					{categoryLabels[category]}
				</p>
			</div>
		);
	};

	return (
		<section className="section-muted py-16">
			<div className="container-width">
				{/* Encabezado */}
				<div className="text-center mb-12">
					<h2 className="text-3xl sm:text-4xl font-bold text-primary mb-4">
						Servicios a Nivel Nacional
					</h2>
					<p className="text-lg text-slate-600 max-w-3xl mx-auto">
						Brindamos soluciones integrales en contabilidad, finanzas, fiscalidad y administración 
						en todas las provincias de República Dominicana. Nuestro compromiso es llegar a donde 
						estés, con la misma calidad y profesionalismo.
					</p>
				</div>

				<div className="grid lg:grid-cols-2 gap-12 items-start">
					{/* Mapa Interactivo */}
					<div className="relative">
						<div className="relative w-full" style={{ paddingBottom: '75%' }}>
							<div className="absolute inset-0">
								{/* Mapa SVG de República Dominicana */}
								<div className="relative w-full h-full">
									<img
										src="/dominican-republic.svg"
										alt="Mapa de República Dominicana"
										className="w-full h-full object-contain"
									/>
									{/* Overlay SVG para resaltar región activa usando paths exactos del mapa */}
									{coveragePoints[currentPointIndex] && regionPaths[coveragePoints[currentPointIndex].name] && (
										<svg
											viewBox="0 0 1000 686"
											className="absolute inset-0 w-full h-full pointer-events-none"
											preserveAspectRatio="xMidYMid meet"
											style={{ zIndex: 1 }}
										>
											{/* Renderizar el path exacto de la región activa */}
											{typeof regionPaths[coveragePoints[currentPointIndex].name] === 'string' && 
												regionPaths[coveragePoints[currentPointIndex].name].startsWith('<') ? (
												// Si es HTML completo, usar dangerouslySetInnerHTML
												<g dangerouslySetInnerHTML={{ __html: regionPaths[coveragePoints[currentPointIndex].name] }} />
											) : (
												// Si es un path 'd', usar directamente
												<path
													d={regionPaths[coveragePoints[currentPointIndex].name]}
													fill="#12284C"
													opacity="0.3"
												/>
											)}
										</svg>
									)}
								</div>
								{/* Capa SVG para marcadores interactivos */}
								<svg
									viewBox="0 0 1000 686"
									className="absolute inset-0 w-full h-full"
									preserveAspectRatio="xMidYMid meet"
									style={{ pointerEvents: 'none', zIndex: 2 }}
								>
										<defs>
											<filter id="shadow">
												<feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.4"/>
											</filter>
											<filter id="glow">
												<feGaussianBlur stdDeviation="3" result="coloredBlur"/>
												<feMerge>
													<feMergeNode in="coloredBlur"/>
													<feMergeNode in="SourceGraphic"/>
												</feMerge>
											</filter>
											{/* Animación de pulso */}
											<animate 
												id="pulse" 
												attributeName="opacity" 
												values="1;0.5;1" 
												dur="2s" 
												repeatCount="indefinite"
											/>
										</defs>
									
									{/* Marcador único animado */}
									{coveragePoints[currentPointIndex] && (
										<g 
											transform={`translate(${coveragePoints[currentPointIndex].x}, ${coveragePoints[currentPointIndex].y})`}
											opacity={isVisible ? 1 : 0}
											style={{
												transition: 'opacity 0.3s ease-in-out'
											}}
										>
											{/* Círculo de pulso animado */}
											<circle
												cx="0"
												cy="0"
												r="25"
												fill="#44797B"
												opacity="0.2"
											>
												<animate
													attributeName="r"
													values="20;30;20"
													dur="1.5s"
													repeatCount="indefinite"
												/>
												<animate
													attributeName="opacity"
													values="0.2;0;0.2"
													dur="1.5s"
													repeatCount="indefinite"
												/>
											</circle>
											
											{/* Sombra del pin */}
											<path
												d="M 2,2 L -4,22 L 8,22 Z"
												fill="rgba(0,0,0,0.2)"
											/>
											
											{/* Pin/Marcador principal */}
											<g>
												<path
													d="M 0,-12 A 7,7 0 0,1 0,2 L -6,20 L 6,20 L 0,2 A 7,7 0 0,1 0,-12 Z"
													fill="#44797B"
													stroke="white"
													strokeWidth="2.5"
													filter="url(#shadow)"
												/>
												{/* Punto interno del pin */}
												<circle
													cx="0"
													cy="-4"
													r="4"
													fill="white"
												/>
											</g>
											
										</g>
									)}
								</svg>
								</div>
							</div>
					</div>

					{/* Estadísticas - Diseño Compacto y Dinámico */}
					<div>
						{isLoading ? (
							<div className="grid grid-cols-2 gap-4">
								{[1, 2, 3, 4].map(i => (
									<div key={i} className="card p-4 border-l-4 animate-pulse" style={{ borderLeftColor: '#12284C' }}>
										<div className="h-8 bg-slate-200 rounded mb-2"></div>
										<div className="h-4 bg-slate-200 rounded w-2/3"></div>
									</div>
								))}
							</div>
						) : hasStats ? (
							<div className="grid grid-cols-2 gap-4">
								{categories.map((category) => {
									const stats = statsData[category] || [];
									if (stats.length === 0) return null;

									return stats.map((stat) => (
										<StatCardWithAnimation 
											key={`${category}-${stat.id}`} 
											stat={stat} 
											category={category} 
										/>
									));
								})}
							</div>
						) : (
							<div className="card p-6 border-l-4 text-center" style={{ borderLeftColor: '#12284C' }}>
								<p className="text-slate-600">Estadísticas próximamente disponibles</p>
							</div>
						)}
					</div>
				</div>

				{/* Mensaje adicional */}
				<div className="mt-12 text-center">
					<div className="bg-gradient-to-br from-primary to-secondary text-white p-8 rounded-lg">
						<h3 className="text-2xl font-bold mb-4">¿Necesitas servicios en tu provincia?</h3>
						<p className="text-lg mb-6 opacity-90">
							Contacta con nosotros sin importar dónde te encuentres. Trabajamos con herramientas digitales 
							modernas para brindarte atención de calidad desde cualquier lugar del país.
						</p>
						<div className="flex gap-4 justify-center flex-wrap">
							<a
								href={`https://wa.me/${WHATSAPP_NUMBER}`}
          target="_blank"
								rel="noreferrer"
								className="px-6 py-3 bg-white text-primary rounded-md font-semibold hover:bg-slate-100 transition-colors"
							>
								Contáctanos por WhatsApp
							</a>
							<a
								href="#contacto"
								className="px-6 py-3 bg-white/20 text-white rounded-md font-semibold hover:bg-white/30 border border-white transition-colors"
							>
								Envíanos un mensaje
							</a>
    </div>
					</div>
				</div>
			</div>
		</section>
	);
}

function ProjectStatsSection() {
	const { data: statsData = {}, isLoading, error } = useQuery({
		queryKey: ['project-statistics'],
		queryFn: contentApi.getProjectStatistics,
		staleTime: 5 * 60 * 1000,
		retry: 1,
	});

	const categoryLabels = {
		constitucion: 'Constitución de Empresas & ONG',
		outsourcing: 'Outsourcing Contable y Fiscal',
		otros: 'Otros Servicios'
	};

	// Sin colores especiales, todas las cards seguirán el mismo diseño

	if (isLoading) {
		return (
			<section className="section-muted">
				<Section id="proyectos" title="Proyectos Completados">
					<div className="grid md:grid-cols-3 gap-6">
						{[1, 2, 3].map(i => (
							<div key={i} className="card p-6 border-l-4 animate-pulse" style={{ borderLeftColor: '#12284C' }}>
								<div className="h-6 bg-slate-200 rounded mb-2 w-2/3"></div>
								<div className="mt-4 space-y-4">
									<div className="h-12 bg-slate-200 rounded mb-3"></div>
									<div className="h-4 bg-slate-200 rounded w-3/4"></div>
								</div>
							</div>
						))}
					</div>
				</Section>
			</section>
		);
	}

	if (error) {
		console.error('Error loading project statistics:', error);
		return null;
	}

	const categories = ['constitucion', 'outsourcing', 'otros'];
	const hasStats = categories.some(cat => statsData[cat] && Array.isArray(statsData[cat]) && statsData[cat].length > 0);

	if (!hasStats) {
		return null;
	}

	return (
		<section className="section-muted">
			<Section id="proyectos" title="Proyectos Completados">
				<div className="grid md:grid-cols-3 gap-6">
					{categories.map((category) => {
						const stats = statsData[category] || [];
						if (stats.length === 0) return null;

						return (
							<div key={category} className="card p-6 border-l-4" style={{ borderLeftColor: '#12284C' }}>
								<h3 className="font-semibold text-primary mb-2">{categoryLabels[category]}</h3>
								<div className="space-y-4 mt-4">
									{stats.map((stat) => (
										<div key={stat.id} className="pb-3 border-b border-slate-100 last:border-0 last:pb-0">
											<div className="flex items-baseline gap-2">
												<span className="text-3xl font-bold text-slate-800">{stat.value}</span>
												{stat.suffix && <span className="text-lg text-slate-600">{stat.suffix}</span>}
											</div>
											<p className="text-slate-700 leading-relaxed mt-1">{stat.title}</p>
										</div>
									))}
								</div>
							</div>
						);
					})}
				</div>
			</Section>
		</section>
	);
}

function ServicesCarousel({ servicios, openModal }) {
	const [currentIndex, setCurrentIndex] = useState(0);
	const [paused, setPaused] = useState(false);
	const [cardsPerView, setCardsPerView] = useState(3);

	// Responsive: ajustar cardsPerView según el ancho de pantalla
	useEffect(() => {
		const handleResize = () => {
			if (window.innerWidth < 768) {
				setCardsPerView(1); // Móvil: 1 card
			} else if (window.innerWidth < 1024) {
				setCardsPerView(2); // Tablet: 2 cards
			} else {
				setCardsPerView(3); // Desktop: 3 cards
			}
			setCurrentIndex(0); // Resetear al cambiar el tamaño
		};

		handleResize(); // Ejecutar al montar
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	// Calcular índice máximo
	const maxIndex = servicios.length > cardsPerView ? servicios.length - cardsPerView : 0;

	// Auto-rotación - avanza de 1 en 1
	useEffect(() => {
		if (!servicios || servicios.length <= cardsPerView || paused) return;
		
		const interval = setInterval(() => {
			setCurrentIndex((prev) => {
				const nextIndex = prev + 1;
				if (nextIndex > maxIndex) {
					return 0;
				}
				return nextIndex;
			});
		}, 5000); // Cambia cada 5 segundos

		return () => clearInterval(interval);
	}, [servicios, paused, maxIndex, cardsPerView]);

	const goNext = () => {
		const nextIndex = currentIndex + 1;
		setCurrentIndex(nextIndex > maxIndex ? 0 : nextIndex);
	};

	const goPrev = () => {
		const prevIndex = currentIndex - 1;
		setCurrentIndex(prevIndex < 0 ? maxIndex : prevIndex);
	};

	if (!servicios || servicios.length === 0) {
		return <div className="text-center text-slate-600">No hay servicios disponibles</div>;
	}

	return (
		<div 
			className="relative"
			onMouseEnter={() => setPaused(true)}
			onMouseLeave={() => setPaused(false)}
			onTouchStart={() => setPaused(true)}
			onTouchEnd={() => setPaused(false)}
		>
			{/* Botones de navegación */}
			{servicios.length > cardsPerView && maxIndex > 0 && (
				<>
					<button 
						onClick={goPrev}
						aria-label="Anterior"
						className="absolute left-0 top-1/2 -translate-y-1/2 bg-white rounded-full p-3 shadow-lg hover:shadow-xl transition-all z-10 hover:scale-110"
					>
						<svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
						</svg>
					</button>
					<button 
						onClick={goNext}
						aria-label="Siguiente"
						className="absolute right-0 top-1/2 -translate-y-1/2 bg-white rounded-full p-3 shadow-lg hover:shadow-xl transition-all z-10 hover:scale-110"
					>
						<svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
						</svg>
					</button>
				</>
			)}

			{/* Contenedor del carrusel */}
			<div className="overflow-hidden w-full relative px-12">
				<div 
					className="flex gap-6 transition-transform duration-500 ease-in-out"
					style={{ 
						transform: `translateX(calc(-${currentIndex} * ((100% - ${(cardsPerView - 1) * 1.5}rem) / ${cardsPerView} + 1.5rem)))`
					}}
				>
					{servicios.map((s, i) => {
						// Cálculo del ancho de cada card
						const gapCount = cardsPerView - 1;
						const cardWidth = `calc((100% - ${gapCount * 1.5}rem) / ${cardsPerView})`;
						return (
							<div 
								key={s.title} 
								className="card p-6 relative overflow-hidden group cursor-pointer transition-all duration-300 hover:shadow-xl"
								style={{ 
									width: cardWidth,
									minWidth: cardWidth,
									maxWidth: cardWidth,
									flexShrink: 0,
									flexGrow: 0,
									boxSizing: 'border-box'
								}}
								onClick={() => openModal(s)}
							>
								{/* Gradiente decorativo en esquina superior derecha - MÁS OSCURO */}
								<div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-primary/40 to-secondary/40 rounded-bl-full transform translate-x-8 -translate-y-8 group-hover:scale-150 transition-transform duration-500"></div>
								
								<div className="relative z-10">
									{/* Icono numerado con gradiente */}
									<div className="w-14 h-14 bg-gradient-to-br from-primary to-secondary rounded-xl flex items-center justify-center text-white font-bold text-xl mb-4 group-hover:scale-110 transition-transform duration-300">
										{i + 1}
									</div>
									
									{/* Título y descripción */}
									<h3 className="font-semibold text-primary mb-2 text-lg">{s.title}</h3>
									<p className="text-slate-700 leading-relaxed mb-4 text-sm">{s.desc}</p>
									
									{/* Botón de más detalles */}
									<div className="flex items-center gap-2 text-sm text-secondary font-medium group-hover:gap-3 transition-all">
										<span>Más detalles</span>
										<span className="group-hover:translate-x-1 transition-transform">→</span>
									</div>
								</div>
							</div>
						);
					})}
				</div>
			</div>

			{/* Indicadores de posición */}
			{servicios.length > cardsPerView && maxIndex > 0 && (
				<div className="flex justify-center gap-2 mt-6">
					{Array.from({ length: maxIndex + 1 }).map((_, i) => {
						const isActive = currentIndex === i;
						return (
							<button
								key={i}
								onClick={() => setCurrentIndex(i)}
								aria-label={`Ir a posición ${i + 1}`}
								className={`h-2 rounded-full transition-all ${
									isActive 
										? 'w-8 bg-primary' 
										: 'w-2 bg-slate-300 hover:bg-slate-400'
								}`}
							/>
						);
					})}
				</div>
			)}
		</div>
	);
}

function WhyChooseUsCarousel({ items }) {
	const [currentIndex, setCurrentIndex] = useState(0);
	const [paused, setPaused] = useState(false);
	const [cardsPerView, setCardsPerView] = useState(3);

	// Responsive: ajustar cardsPerView según el ancho de pantalla
	useEffect(() => {
		const handleResize = () => {
			if (window.innerWidth < 768) {
				setCardsPerView(1); // Móvil: 1 card
			} else if (window.innerWidth < 1024) {
				setCardsPerView(2); // Tablet: 2 cards
			} else {
				setCardsPerView(3); // Desktop: 3 cards
			}
			setCurrentIndex(0); // Resetear al cambiar el tamaño
		};

		handleResize(); // Ejecutar al montar
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	// Calcular índice máximo
	const maxIndex = items.length > cardsPerView ? items.length - cardsPerView : 0;

	// Auto-rotación - avanza de 1 en 1
	useEffect(() => {
		if (!items || items.length <= cardsPerView || paused) return;
		
		const interval = setInterval(() => {
			setCurrentIndex((prev) => {
				const nextIndex = prev + 1;
				if (nextIndex > maxIndex) {
					return 0;
				}
				return nextIndex;
			});
		}, 5000); // Cambia cada 5 segundos

		return () => clearInterval(interval);
	}, [items, paused, maxIndex, cardsPerView]);

	const goNext = () => {
		const nextIndex = currentIndex + 1;
		setCurrentIndex(nextIndex > maxIndex ? 0 : nextIndex);
	};

	const goPrev = () => {
		const prevIndex = currentIndex - 1;
		setCurrentIndex(prevIndex < 0 ? maxIndex : prevIndex);
	};

	if (!items || items.length === 0) {
		return <div className="text-center text-slate-600">No hay elementos disponibles</div>;
	}

	return (
		<div 
			className="relative"
			onMouseEnter={() => setPaused(true)}
			onMouseLeave={() => setPaused(false)}
			onTouchStart={() => setPaused(true)}
			onTouchEnd={() => setPaused(false)}
		>
			{/* Botones de navegación */}
			{items.length > cardsPerView && maxIndex > 0 && (
				<>
					<button 
						onClick={goPrev}
						aria-label="Anterior"
						className="absolute left-0 top-1/2 -translate-y-1/2 bg-white rounded-full p-3 shadow-lg hover:shadow-xl transition-all z-10 hover:scale-110"
					>
						<svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
						</svg>
					</button>
					<button 
						onClick={goNext}
						aria-label="Siguiente"
						className="absolute right-0 top-1/2 -translate-y-1/2 bg-white rounded-full p-3 shadow-lg hover:shadow-xl transition-all z-10 hover:scale-110"
					>
						<svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
						</svg>
					</button>
				</>
			)}

			{/* Contenedor del carrusel */}
			<div className="overflow-hidden w-full relative px-12">
				<div 
					className="flex gap-6 transition-transform duration-500 ease-in-out"
					style={{ 
						transform: `translateX(calc(-${currentIndex} * ((100% - ${(cardsPerView - 1) * 1.5}rem) / ${cardsPerView} + 1.5rem)))`
					}}
				>
					{items.map((item, i) => {
						// Cálculo del ancho de cada card
						const gapCount = cardsPerView - 1;
						const cardWidth = `calc((100% - ${gapCount * 1.5}rem) / ${cardsPerView})`;
						const IconComponent = item.icon;
						return (
							<div 
								key={i} 
								className="card p-6 relative overflow-hidden group transition-all duration-300 hover:shadow-xl"
								style={{ 
									width: cardWidth,
									minWidth: cardWidth,
									maxWidth: cardWidth,
									flexShrink: 0,
									flexGrow: 0,
									boxSizing: 'border-box'
								}}
							>
								{/* Gradiente decorativo en esquina superior derecha - MÁS OSCURO */}
								<div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-primary/40 to-secondary/40 rounded-bl-full transform translate-x-8 -translate-y-8 group-hover:scale-150 transition-transform duration-500"></div>
								
								<div className="relative z-10">
									{/* Icono con gradiente */}
									<div className="w-14 h-14 bg-gradient-to-br from-primary to-secondary rounded-xl flex items-center justify-center text-white mb-4 group-hover:scale-110 transition-transform duration-300">
										<IconComponent className="w-8 h-8" />
									</div>
									
									{/* Título y descripción */}
									<h3 className="font-semibold text-primary mb-2 text-lg">{item.title}</h3>
									<p className="text-slate-700 leading-relaxed text-sm">{item.desc}</p>
								</div>
							</div>
						);
					})}
				</div>
			</div>

			{/* Indicadores de posición */}
			{items.length > cardsPerView && maxIndex > 0 && (
				<div className="flex justify-center gap-2 mt-6">
					{Array.from({ length: maxIndex + 1 }).map((_, i) => {
						const isActive = currentIndex === i;
						return (
							<button
								key={i}
								onClick={() => setCurrentIndex(i)}
								aria-label={`Ir a posición ${i + 1}`}
								className={`h-2 rounded-full transition-all ${
									isActive 
										? 'w-8 bg-primary' 
										: 'w-2 bg-slate-300 hover:bg-slate-400'
								}`}
							/>
						);
					})}
				</div>
			)}
		</div>
	);
}

function EmpresasCarpetaHomeCarousel({ config }) {
	const items = (config?.items || []).filter((i) => i && i.activa && i.en_carusel);
	const maxItems = Number(config?.carousel?.max_items || 8);
	const shown = items.slice(0, maxItems);
	const title = config?.carousel?.title || 'Empresas en carpeta';
	const subtitle = config?.carousel?.subtitle || 'Empresas disponibles para evaluación';

	const [currentIndex, setCurrentIndex] = useState(0);
	const [paused, setPaused] = useState(false);
	const [cardsPerView, setCardsPerView] = useState(3);

	useEffect(() => {
		const handleResize = () => {
			if (window.innerWidth < 768) {
				setCardsPerView(1);
			} else if (window.innerWidth < 1024) {
				setCardsPerView(2);
			} else {
				setCardsPerView(3);
			}
			setCurrentIndex(0);
		};
		handleResize();
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	const maxIndex = shown.length > cardsPerView ? shown.length - cardsPerView : 0;

	useEffect(() => {
		if (!shown.length || shown.length <= cardsPerView || paused) return;
		const interval = setInterval(() => {
			setCurrentIndex((prev) => {
				const nextIndex = prev + 1;
				return nextIndex > maxIndex ? 0 : nextIndex;
			});
		}, 5000);
		return () => clearInterval(interval);
	}, [shown.length, paused, maxIndex, cardsPerView]);

	const goNext = () => {
		const nextIndex = currentIndex + 1;
		setCurrentIndex(nextIndex > maxIndex ? 0 : nextIndex);
	};

	const goPrev = () => {
		const prevIndex = currentIndex - 1;
		setCurrentIndex(prevIndex < 0 ? maxIndex : prevIndex);
	};

	if (!config?.carousel?.enabled || shown.length === 0) {
		return null;
	}

	const money = (v) =>
		new Intl.NumberFormat('es-DO', { style: 'currency', currency: 'DOP', minimumFractionDigits: 2 }).format(Number(v || 0));

	const formatFechaCorta = (iso) => {
		if (!iso) return 'N/D';
		try {
			const d = new Date(`${iso}T12:00:00`);
			return d.toLocaleDateString('es-DO', { day: 'numeric', month: 'short', year: 'numeric' });
		} catch {
			return iso;
		}
	};

	return (
		<section className="section-muted">
			<div id="empresas-carpeta-home" className="container-width py-16">
				<div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between mb-6">
					<div>
						<h2 className="text-3xl sm:text-4xl font-bold text-primary">{title}</h2>
						<p className="text-slate-600 mt-2">{subtitle}</p>
					</div>
					<Link
						to="/empresas-en-carpeta"
						className="shrink-0 self-end sm:self-start inline-flex items-center gap-2 px-4 py-2 rounded-lg border-2 border-primary text-slate-500 hover:bg-primary hover:text-white transition-colors"
					>
						Ver todo
					</Link>
				</div>

				<div
					className="relative"
					onMouseEnter={() => setPaused(true)}
					onMouseLeave={() => setPaused(false)}
					onTouchStart={() => setPaused(true)}
					onTouchEnd={() => setPaused(false)}
				>
					{shown.length > cardsPerView && maxIndex > 0 && (
						<>
							<button
								type="button"
								onClick={goPrev}
								aria-label="Anterior"
								className="absolute left-0 top-1/2 -translate-y-1/2 bg-white rounded-full p-3 shadow-lg hover:shadow-xl transition-all z-10 hover:scale-110"
							>
								<svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
								</svg>
							</button>
							<button
								type="button"
								onClick={goNext}
								aria-label="Siguiente"
								className="absolute right-0 top-1/2 -translate-y-1/2 bg-white rounded-full p-3 shadow-lg hover:shadow-xl transition-all z-10 hover:scale-110"
							>
								<svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
								</svg>
							</button>
						</>
					)}

					<div className="overflow-hidden w-full relative px-12">
						<div
							className="flex gap-6 transition-transform duration-500 ease-in-out"
							style={{
								transform: `translateX(calc(-${currentIndex} * ((100% - ${(cardsPerView - 1) * 1.5}rem) / ${cardsPerView} + 1.5rem)))`,
							}}
						>
							{shown.map((e) => {
								const gapCount = cardsPerView - 1;
								const cardWidth = `calc((100% - ${gapCount * 1.5}rem) / ${cardsPerView})`;
								return (
									<Link
										key={`${e.id}-${e.nombre_empresa}`}
										to="/empresas-en-carpeta"
										className="group relative flex flex-col rounded-2xl border border-slate-200/90 bg-white shadow-md ring-1 ring-slate-900/5 hover:shadow-2xl hover:-translate-y-1.5 hover:ring-primary/20 transition-all duration-300 overflow-hidden flex-shrink-0 flex-grow-0 text-left"
										style={{
											width: cardWidth,
											minWidth: cardWidth,
											maxWidth: cardWidth,
											boxSizing: 'border-box',
										}}
									>
										<div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-primary/30 via-secondary/15 to-transparent rounded-bl-[2.5rem] pointer-events-none group-hover:scale-110 transition-transform duration-500" />
										<div className="h-1.5 w-full bg-gradient-to-r from-primary via-secondary to-primary/70 shrink-0" />
										<div className="relative p-5 flex flex-col flex-1 min-h-[240px]">
											<span className="inline-flex self-start items-center gap-1.5 rounded-full bg-gradient-to-r from-primary/12 to-secondary/10 text-primary text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 mb-3 ring-1 ring-primary/15">
												<TagIcon className="h-3.5 w-3.5" aria-hidden />
												Oportunidad
											</span>
											<div className="flex items-start gap-2.5 mb-3">
												<div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary group-hover:bg-primary group-hover:text-white transition-colors duration-300">
													<BuildingOffice2Icon className="h-5 w-5" aria-hidden />
												</div>
												<h3 className="font-bold text-slate-900 text-sm uppercase leading-snug tracking-tight line-clamp-2 group-hover:text-primary transition-colors">
													{e.nombre_empresa}
												</h3>
											</div>
											<p className="text-xs text-slate-600 leading-relaxed line-clamp-3 mb-4 flex-1">
												{e.actividad || 'Actividad comercial disponible bajo consulta.'}
											</p>
											<div className="space-y-2 text-xs text-slate-500 mb-4">
												<div className="flex items-start gap-2">
													<MapPinIcon className="h-4 w-4 mt-0.5 shrink-0 text-secondary" aria-hidden />
													<span className="line-clamp-2">{e.ubicacion || 'Ubicación por confirmar'}</span>
												</div>
												<div className="flex items-start gap-2">
													<CalendarDaysIcon className="h-4 w-4 mt-0.5 shrink-0 text-primary" aria-hidden />
													<span>Constitución: {formatFechaCorta(e.fecha_constitucion)}</span>
												</div>
											</div>
											<div className="mt-auto pt-4 border-t border-slate-100 flex items-end justify-between gap-2">
												<div>
													<p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-0.5">Inversión</p>
													<p className="text-xl font-extrabold text-primary tabular-nums">{money(e.costo)}</p>
												</div>
												<span className="inline-flex items-center gap-0.5 text-xs font-semibold text-secondary group-hover:gap-1 transition-all">
													Ver catálogo
													<ArrowRightIcon className="h-4 w-4" aria-hidden />
												</span>
											</div>
										</div>
									</Link>
								);
							})}
						</div>
					</div>

					{shown.length > cardsPerView && maxIndex > 0 && (
						<div className="flex justify-center gap-2 mt-6">
							{Array.from({ length: maxIndex + 1 }).map((_, i) => {
								const isActive = currentIndex === i;
								return (
									<button
										type="button"
										key={i}
										onClick={() => setCurrentIndex(i)}
										aria-label={`Ir a posición ${i + 1}`}
										className={`h-2 rounded-full transition-all ${
											isActive ? 'w-8 bg-primary' : 'w-2 bg-slate-300 hover:bg-slate-400'
										}`}
									/>
								);
							})}
						</div>
					)}
				</div>
			</div>
		</section>
	);
}

function TeamMemberModal({ isOpen, onClose, member }) {
	if (!isOpen || !member) return null;

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
			{/* Overlay */}
			<div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose}></div>
			
			{/* Modal */}
			<div 
				className="relative bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto z-10"
				onClick={(e) => e.stopPropagation()}
			>
				{/* Header */}
				<div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between rounded-t-xl">
					<h2 className="text-2xl font-bold text-primary">{member.name}</h2>
					<button
						onClick={onClose}
						className="text-slate-400 hover:text-slate-600 transition-colors"
						aria-label="Cerrar"
					>
						<XMarkIcon className="w-6 h-6" />
					</button>
				</div>

				{/* Content */}
				<div className="p-6">
					<div className="flex flex-col md:flex-row gap-6 items-start">
						{/* Foto más pequeña */}
						<div className="flex-shrink-0 mx-auto md:mx-0">
							<div className="w-48 h-64 flex items-end justify-center">
								<img
									src={member.image}
									alt={member.name}
									className="w-full h-full object-contain object-bottom filter drop-shadow-lg"
								/>
							</div>
						</div>

						{/* Información */}
						<div className="flex-1">
							<p className="text-secondary font-semibold text-sm uppercase tracking-wide mb-4">{member.position}</p>
							<div className="prose prose-sm max-w-none">
								<p className="text-slate-700 leading-relaxed">{member.bio}</p>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

function HomePage({ openModal, openTeamModal }) {
	const location = useLocation();
	const [empresasCarpetaConfig, setEmpresasCarpetaConfig] = useState(null);

	// Manejar scroll cuando se llega desde otra página con hash (ej: /#servicios)
	useEffect(() => {
		if (location.hash) {
			const sectionId = location.hash.substring(1); // Remover el #
			setTimeout(() => {
				const element = document.getElementById(sectionId);
				if (element) {
					element.scrollIntoView({ behavior: 'smooth' });
				}
			}, 100); // Pequeño delay para asegurar que el DOM esté listo
		}
	}, [location.hash]);

	// Manejar servicio pendiente desde el navbar (cuando se navega desde otra página)
	useEffect(() => {
		const pendingService = sessionStorage.getItem('pendingService');
		if (pendingService && openModal) {
			try {
				const service = JSON.parse(pendingService);
				sessionStorage.removeItem('pendingService');
				// Pequeño delay para asegurar que todo esté renderizado
				setTimeout(() => {
					openModal(service);
				}, 200);
			} catch (e) {
				console.error('Error parsing pending service:', e);
				sessionStorage.removeItem('pendingService');
			}
		}
	}, [openModal]);

	useEffect(() => {
		(async () => {
			try {
				const data = await contentApi.getEmpresasCarpetaData();
				setEmpresasCarpetaConfig(data);
			} catch (e) {
				setEmpresasCarpetaConfig(null);
			}
		})();
	}, []);

	return (
		<>
			<Hero />
			<NationalCoverageSection />
            {/* CTA global eliminado por solicitud */}

            <Section id="servicios" title="Servicios">
				<ServicesCarousel servicios={serviciosList} openModal={openModal} />
			</Section>
			<EmpresasCarpetaHomeCarousel config={empresasCarpetaConfig} />
			<section className="section-muted">
				<TestimonialsSection />
			</section>
			<PartnersSection />
			{/* ProjectStatsSection ahora está integrado en NationalCoverageSection */}
			<Section id="nosotros" title="Sobre Nosotros">
				<blockquote className="text-2xl font-semibold text-primary mb-8 text-left">"Más que asesores, somos tus aliados."</blockquote>
				<div className="grid md:grid-cols-3 gap-6 mt-8">
					{/* Misión */}
					<div className="p-6 relative overflow-hidden group transition-all duration-300 hover:shadow-xl rounded-xl border shadow-sm" style={{ backgroundColor: '#12284C', color: '#FFFFFF', zIndex: 1 }}>
						{/* Gradiente decorativo en esquina superior derecha */}
						<div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-white/20 to-white/10 rounded-bl-full transform translate-x-8 -translate-y-8 group-hover:scale-150 transition-transform duration-500" style={{ zIndex: 0 }}></div>
						
						<div className="relative" style={{ zIndex: 10 }}>
							{/* Título */}
							<h3 className="font-bold text-xl mb-4 uppercase" style={{ color: '#FFFFFF' }}>Misión</h3>
							{/* Descripción */}
							<p className="leading-relaxed" style={{ color: '#FFFFFF' }}>Garantizar a nuestros clientes un servicio personalizado de consultoría integral, confiable y eficiente, creando soluciones a la medida de sus necesidades y expectativas de desarrollo; ayudando a minimizar las cargas administrativas y motivando el crecimiento de su empresa.</p>
						</div>
					</div>
					{/* Visión */}
					<div className="p-6 relative overflow-hidden group transition-all duration-300 hover:shadow-xl rounded-xl border shadow-sm" style={{ backgroundColor: '#12284C', color: '#FFFFFF', zIndex: 1 }}>
						{/* Gradiente decorativo en esquina superior derecha */}
						<div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-white/20 to-white/10 rounded-bl-full transform translate-x-8 -translate-y-8 group-hover:scale-150 transition-transform duration-500" style={{ zIndex: 0 }}></div>
						
						<div className="relative" style={{ zIndex: 10 }}>
							{/* Título */}
							<h3 className="font-bold text-xl mb-4 uppercase" style={{ color: '#FFFFFF' }}>Visión</h3>
							{/* Descripción */}
							<p className="leading-relaxed" style={{ color: '#FFFFFF' }}>Consolidar nuestra firma como un despacho que brinda soluciones óptimas, competitivas y eficientes; encaminados al éxito e inspirando la confianza, el respeto y la honestidad, sustentada principalmente en la satisfacción de nuestros clientes.</p>
						</div>
					</div>
					{/* Valores */}
					<div className="p-6 relative overflow-hidden group transition-all duration-300 hover:shadow-xl rounded-xl border shadow-sm" style={{ backgroundColor: '#12284C', color: '#FFFFFF', zIndex: 1 }}>
						{/* Gradiente decorativo en esquina superior derecha */}
						<div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-white/20 to-white/10 rounded-bl-full transform translate-x-8 -translate-y-8 group-hover:scale-150 transition-transform duration-500" style={{ zIndex: 0 }}></div>
						
						<div className="relative" style={{ zIndex: 10 }}>
							{/* Título */}
							<h3 className="font-bold text-xl mb-4 uppercase" style={{ color: '#FFFFFF' }}>Valores</h3>
							{/* Lista de valores */}
							<ul className="space-y-2 leading-relaxed" style={{ color: '#FFFFFF' }}>
								<li>Responsabilidad</li>
								<li>Integridad</li>
								<li>Confianza</li>
								<li>Cooperación</li>
								<li>Ética Profesional</li>
								<li style={{ color: '#FFFFFF' }}>Puntualidad</li>
							</ul>
						</div>
					</div>
				</div>

				{/* Directivos */}
				<div className="mt-16 pt-12 border-t border-slate-200">
					<h2 className="text-3xl font-bold text-primary mb-4 text-center">Nuestro Equipo Directivo</h2>
					<p className="text-slate-600 text-center mb-12 max-w-2xl mx-auto">
						Conoce a los profesionales que lideran PAULINOSOSA Asesores & Consultores, comprometidos con brindarte soluciones de excelencia.
					</p>
					<div className="grid md:grid-cols-3 gap-10 max-w-6xl mx-auto">
						{/* CEO */}
						<div className="text-center group">
							<div className="relative mb-6 mx-auto w-72 h-96 flex items-end justify-center">
								{/* Degradado sutil de fondo */}
								<div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent rounded-t-3xl"></div>
								{/* Imagen */}
								<img
									src="/ceo.png"
									alt="Juan Carlos Paulino - CEO"
									className="relative z-10 w-full h-full object-contain object-bottom filter drop-shadow-2xl group-hover:scale-105 transition-all duration-300"
									style={{ maxHeight: '100%' }}
								/>
							</div>
							<div className="space-y-2">
								<button 
									onClick={() => openTeamModal({
										name: 'Lic. Juan Carlos Paulino',
										position: 'CEO',
										image: '/ceo.png',
										bio: 'Licenciado en Contabilidad con amplia experiencia en gestión empresarial y consultoría financiera. Especialista en constitución de empresas, planificación fiscal y estrategias de crecimiento. Lidera el equipo con visión estratégica y compromiso con la excelencia en el servicio al cliente.'
									})}
									className="text-xl font-bold text-primary mb-1 hover:text-secondary transition-colors cursor-pointer"
								>
									Lic. Juan Carlos Paulino
								</button>
								<p className="text-secondary font-semibold text-sm uppercase tracking-wide">CEO</p>
								<div className="h-1 w-20 bg-gradient-to-r from-transparent via-primary to-transparent mx-auto mt-4"></div>
							</div>
						</div>

						{/* Directora Administrativa */}
						<div className="text-center group">
							<div className="relative mb-6 mx-auto w-72 h-96 flex items-end justify-center">
								{/* Degradado sutil de fondo */}
								<div className="absolute inset-0 bg-gradient-to-b from-secondary/5 via-transparent to-transparent rounded-t-3xl"></div>
								{/* Imagen */}
								<img
									src="/direct_adm.png"
									alt="Mirita I Sosa - Directora Administrativa"
									className="relative z-10 w-full h-full object-contain object-bottom filter drop-shadow-2xl group-hover:scale-105 transition-all duration-300"
									style={{ maxHeight: '100%' }}
								/>
							</div>
							<div className="space-y-2">
								<button 
									onClick={() => openTeamModal({
										name: 'Licda. Mirita I Sosa',
										position: 'Directora Administrativa',
										image: '/direct_adm.png',
										bio: 'Licenciada en Administración de Empresas con especialización en gestión administrativa y operativa. Experta en procesos administrativos, gestión documental y coordinación de equipos. Su enfoque en la eficiencia y organización garantiza un servicio administrativo de calidad.'
									})}
									className="text-xl font-bold text-primary mb-1 hover:text-secondary transition-colors cursor-pointer"
								>
									Licda. Mirita I Sosa
								</button>
								<p className="text-secondary font-semibold text-sm uppercase tracking-wide">Directora Administrativa</p>
								<div className="h-1 w-20 bg-gradient-to-r from-transparent via-secondary to-transparent mx-auto mt-4"></div>
							</div>
						</div>

						{/* Asesor Legal */}
						<div className="text-center group">
							<div className="relative mb-6 mx-auto w-72 h-96 flex items-end justify-center">
								{/* Degradado sutil de fondo */}
								<div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent rounded-t-3xl"></div>
								{/* Imagen */}
								<img
									src="/asesor_legal.png"
									alt="Félix Núñez - Asesor Legal"
									className="relative z-10 w-full h-full object-contain object-bottom filter drop-shadow-2xl group-hover:scale-105 transition-all duration-300"
									style={{ maxHeight: '100%' }}
								/>
							</div>
							<div className="space-y-2">
								<button 
									onClick={() => openTeamModal({
										name: 'Lic. Félix Núñez',
										position: 'Asesor Legal',
										image: '/asesor_legal.png',
										bio: 'Licenciado en Derecho con especialización en derecho empresarial y comercial. Experto en constitución de sociedades, gestión de registros mercantiles y asesoría legal corporativa. Proporciona orientación legal precisa y soluciones jurídicas adaptadas a las necesidades de cada cliente.'
									})}
									className="text-xl font-bold text-primary mb-1 hover:text-secondary transition-colors cursor-pointer"
								>
									Lic. Félix Núñez
								</button>
								<p className="text-secondary font-semibold text-sm uppercase tracking-wide">Asesor Legal</p>
								<div className="h-1 w-20 bg-gradient-to-r from-transparent via-primary to-transparent mx-auto mt-4"></div>
							</div>
						</div>
					</div>
				</div>
			</Section>
			<section className="section-muted">
				<Section id="objetivos" title="Objetivos">
					<p className="text-lg leading-relaxed">Garantizar a nuestros clientes un servicio personalizado de consultoría integral, confiable y eficiente, creando trajes a la medida de sus necesidades y expectativas de crecimiento, ayudando a minimizar las cargas administrativas y motivando el crecimiento de su negocio.</p>
				</Section>
			</section>
			<Section id="elegirnos" title="¿Por qué Elegirnos?">
				<div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
					{[
						{
							icon: MegaphoneIcon,
							title: 'Atención Personalizada',
							desc: 'Nos adaptamos a tus necesidades específicas, ofreciendo soluciones a medida.'
						},
						{
							icon: ChatBubbleLeftRightIcon,
							title: 'Asesoría Constante',
							desc: 'Siempre a tu lado, brindando orientación experta y actualizada.'
						},
						{
							icon: InformationCircleIcon,
							title: 'Información Disponible',
							desc: 'Acceso claro y oportuno a toda la información relevante para tus decisiones.'
						},
						{
							icon: ShieldCheckIcon,
							title: 'Seguridad & Confidencialidad',
							desc: 'Protegemos tus datos y operaciones con los más altos estándares.'
						},
						{
							icon: ChartPieIcon,
							title: 'Estrategia Financiera',
							desc: 'Diseñamos planes financieros sólidos para optimizar tus recursos.'
						},
						{
							icon: PuzzlePieceIcon,
							title: 'Estrategia Fiscal',
							desc: 'Optimizamos tu carga tributaria de forma legal y eficiente.'
						},
						{
							icon: BanknotesIcon,
							title: 'Ahorro',
							desc: 'Te ayudamos a maximizar tus beneficios y reducir costos innecesarios.'
						}
					].map((item, i) => {
						const IconComponent = item.icon;
						return (
							<div 
								key={i} 
								className="card p-6 relative overflow-hidden group transition-all duration-300 hover:shadow-xl"
							>
								{/* Gradiente decorativo en esquina superior derecha - MÁS OSCURO */}
								<div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-primary/40 to-secondary/40 rounded-bl-full transform translate-x-8 -translate-y-8 group-hover:scale-150 transition-transform duration-500"></div>
								
								<div className="relative z-10">
									{/* Icono con gradiente */}
									<div className="w-14 h-14 bg-gradient-to-br from-primary to-secondary rounded-xl flex items-center justify-center text-white mb-4 group-hover:scale-110 transition-transform duration-300">
										<IconComponent className="w-8 h-8" />
									</div>
									
									{/* Título y descripción */}
									<h3 className="font-semibold text-primary mb-2 text-lg">{item.title}</h3>
									<p className="text-slate-700 leading-relaxed text-sm">{item.desc}</p>
								</div>
							</div>
						);
					})}
				</div>
			</Section>
			<section className="section-muted">
				<FAQSection />
			</section>
			<section id="contacto" className="container-width py-16">
				<h2 className="text-3xl sm:text-4xl font-bold mb-6 text-primary">Contáctanos</h2>
				<ContactForm />
			</section>
			<footer className="bg-slate-50 border-t">
				<div className="container-width py-10 grid sm:grid-cols-2 md:grid-cols-4 gap-6">
					<div>
						<h3 className="font-semibold text-primary mb-2">Teléfono</h3>
						<a href="tel:+18496079129" className="text-slate-700">(849) 607-9129</a>
					</div>
					<div>
						<h3 className="font-semibold text-primary mb-2">Sitio</h3>
						<a href="https://www.psasesoresconsultores.com" className="text-slate-700">psasesoresconsultores.com</a>
					</div>
					<div>
						<h3 className="font-semibold text-primary mb-2">Email</h3>
						<a href={`mailto:${CONTACT_EMAIL}`} className="text-slate-700">{CONTACT_EMAIL}</a>
					</div>
					<div>
						<h3 className="font-semibold text-primary mb-2">Ubicación</h3>
						<a href="https://maps.app.goo.gl/vRakdwnc7qw4kEXV7" target="_blank" rel="noreferrer" className="text-slate-700">Ver en mapa</a>
					</div>
				</div>
				<div className="container-width pb-6 flex flex-col items-center gap-3">
					<div className="flex items-center gap-4 text-primary">
					<a href={SOCIAL_FACEBOOK} target="_blank" rel="noreferrer" aria-label="Facebook" className="hover:opacity-80">
						<FacebookIcon className="w-6 h-6" />
						</a>
					<a href={SOCIAL_INSTAGRAM} target="_blank" rel="noreferrer" aria-label="Instagram" className="hover:opacity-80">
						<InstagramIcon className="w-6 h-6" />
					</a>
					<a href={SOCIAL_THREADS} target="_blank" rel="noreferrer" aria-label="Threads" className="hover:opacity-80">
						<ThreadsIcon className="w-6 h-6" />
					</a>
					<a href={SOCIAL_LINKEDIN} target="_blank" rel="noreferrer" aria-label="LinkedIn" className="hover:opacity-80">
						<LinkedinIcon className="w-6 h-6" />
					</a>
					<a href={SOCIAL_TIKTOK} target="_blank" rel="noreferrer" aria-label="TikTok" className="hover:opacity-80">
						<TikTokIcon className="w-6 h-6" />
					</a>
					</div>
					<div className="text-center text-xs text-slate-500 space-x-4">
					<span>© {new Date().getFullYear()} Paulino Sosa Asesores & Consultores</span>
					<Link to="/privacidad" className="underline">Privacidad</Link>
					<Link to="/terminos" className="underline">Términos</Link>
					<Link to="/cookies" className="underline">Cookies</Link>
					</div>
				</div>
			</footer>
		</>
	);
}

function BlogList() {
	const [selectedCategory, setSelectedCategory] = useState(null);
	const [searchQuery, setSearchQuery] = useState('');
	const [isSearching, setIsSearching] = useState(false);
	
	// Query para posts normales (por categoría)
	const { data: posts = [], isLoading } = useQuery({
		queryKey: ['blogPosts', selectedCategory],
		queryFn: () => contentApi.getBlogPosts(selectedCategory),
		staleTime: 5 * 60 * 1000,
		enabled: !isSearching && !searchQuery,
	});
	
	// Query para búsqueda
	const { data: searchResults = null, isLoading: isSearchLoading } = useQuery({
		queryKey: ['blogSearch', searchQuery],
		queryFn: () => contentApi.searchBlogPosts(searchQuery),
		staleTime: 5 * 60 * 1000,
		enabled: isSearching && searchQuery.length > 0,
	});
	
	const handleSearch = (e) => {
		e.preventDefault();
		const query = searchQuery.trim();
		if (query.length > 0) {
			setIsSearching(true);
			setSelectedCategory(null);
		} else {
			setIsSearching(false);
		}
	};
	
	const clearSearch = () => {
		setSearchQuery('');
		setIsSearching(false);
	};

	const categories = [
		{ value: null, label: 'Todos' },
		{ value: 'contabilidad', label: 'Contabilidad' },
		{ value: 'finanzas', label: 'Finanzas' },
		{ value: 'fiscalidad', label: 'Fiscalidad' },
		{ value: 'administracion', label: 'Administración' },
		{ value: 'constitucion', label: 'Constitución' },
		{ value: 'legal', label: 'Legal' },
		{ value: 'consultoria', label: 'Consultoría' },
		{ value: 'general', label: 'General' },
	];

	const formatDate = (dateString) => {
		if (!dateString) return '';
		const date = new Date(dateString);
		return date.toLocaleDateString('es-DO', { year: 'numeric', month: 'long', day: 'numeric' });
	};

	const displayPosts = isSearching && searchResults ? searchResults.results || [] : posts;
	const isLoadingPosts = isSearching ? isSearchLoading : isLoading;

	return (
		<div className="container-width py-12">
			<div className="text-center mb-10">
				<h1 className="text-4xl sm:text-5xl font-bold text-primary mb-4">Blog</h1>
				<p className="text-lg text-slate-600 max-w-2xl mx-auto">
					Artículos, noticias y discusiones sobre contabilidad, finanzas, fiscalidad y administración
				</p>
			</div>

			{/* Barra de búsqueda */}
			<div className="max-w-2xl mx-auto mb-8">
				<form onSubmit={handleSearch} className="relative">
					<input
						type="text"
						value={searchQuery}
						onChange={(e) => {
							setSearchQuery(e.target.value);
							if (e.target.value.trim().length === 0) {
								setIsSearching(false);
							}
						}}
						placeholder="Buscar artículos..."
						className="w-full px-4 py-3 pr-12 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
					/>
					<button
						type="submit"
						className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-primary hover:text-secondary transition-colors"
						aria-label="Buscar"
					>
						<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
						</svg>
					</button>
				</form>
				{isSearching && searchQuery && (
					<div className="mt-3 flex items-center justify-between">
						<p className="text-sm text-slate-600">
							{isSearchLoading ? 'Buscando...' : searchResults ? `${searchResults.count} resultado(s) para "${searchResults.query}"` : ''}
						</p>
						<button
							onClick={clearSearch}
							className="text-sm text-primary hover:text-secondary underline"
						>
							Limpiar búsqueda
						</button>
					</div>
				)}
			</div>

			{/* Filtro de categorías */}
			{!isSearching && (
				<div className="flex flex-wrap gap-3 justify-center mb-10">
				{categories.map((cat) => (
					<button
						key={cat.value || 'all'}
						onClick={() => setSelectedCategory(cat.value)}
						className={`px-4 py-2 rounded-md transition-all ${
							selectedCategory === cat.value
								? 'bg-primary text-white'
								: 'bg-white text-primary border border-primary hover:bg-primary hover:text-white'
						}`}
					>
						{cat.label}
					</button>
				))}
			</div>
			)}

			{isLoadingPosts ? (
				<div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
					{[1, 2, 3].map((i) => (
						<div key={i} className="card animate-pulse">
							<div className="h-48 bg-slate-200 rounded-t-lg"></div>
							<div className="p-6 space-y-3">
								<div className="h-4 bg-slate-200 rounded w-3/4"></div>
								<div className="h-4 bg-slate-200 rounded w-1/2"></div>
								<div className="h-20 bg-slate-200 rounded"></div>
							</div>
						</div>
					))}
				</div>
			) : displayPosts.length === 0 ? (
				<div className="text-center py-16">
					<p className="text-slate-600 text-lg">
						{isSearching ? `No se encontraron artículos para "${searchQuery}"` : 'No hay artículos publicados aún.'}
					</p>
				</div>
			) : (
				<div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
					{displayPosts.map((post) => (
						<Link
							key={post.id}
							to={`/blog/${post.slug}`}
							className="card overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
						>
							{post.featured_image_url && (
								<img
									src={post.featured_image_url}
									alt={post.title}
									className="w-full h-48 object-cover"
								/>
							)}
							<div className="p-6">
								<div className="flex items-center gap-2 mb-3">
									<span className="text-xs px-2 py-1 bg-primary/10 text-primary rounded-full">
										{post.category_display}
									</span>
									{post.published_at && (
										<span className="text-xs text-slate-500">{formatDate(post.published_at)}</span>
									)}
								</div>
								<h3 className="font-bold text-xl text-primary mb-2 line-clamp-2">{post.title}</h3>
								<p className="text-slate-600 text-sm line-clamp-3 mb-4">{post.excerpt}</p>
								<div className="flex items-center justify-between">
									<span className="text-sm text-slate-500">{post.author}</span>
									{post.views > 0 && (
										<span className="text-sm text-slate-500">{post.views} vistas</span>
									)}
								</div>
								{post.tags_list && post.tags_list.length > 0 && (
									<div className="flex flex-wrap gap-2 mt-4">
										{post.tags_list.slice(0, 3).map((tag, idx) => (
											<span key={idx} className="text-xs px-2 py-1 bg-slate-100 text-slate-600 rounded">
												#{tag}
											</span>
										))}
									</div>
								)}
							</div>
						</Link>
					))}
				</div>
			)}
		</div>
	);
}

// Función para formatear el contenido del blog
function formatBlogContent(content) {
	if (!content) return '';
	
	// Detectar si el contenido ya es HTML válido
	// Si tiene cualquier etiqueta HTML (apertura o cierre), asumimos que es HTML
	const htmlTagPattern = /<\/?[a-z][a-z0-9]*[^>]*>/i;
	const hasHtmlTags = htmlTagPattern.test(content);
	
	// Si tiene etiquetas HTML, retornar tal cual SIN procesar
	if (hasHtmlTags) {
		// Es HTML válido, retornar tal cual
		return content;
	}
	
	// Si no tiene HTML, convertir saltos de línea a HTML
	// Normalizar saltos de línea
	let formatted = content.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
	
	// Dividir por dobles saltos de línea (párrafos)
	const paragraphs = formatted.split(/\n\s*\n+/);
	
	if (paragraphs.length > 1) {
		// Hay múltiples párrafos
		formatted = paragraphs
			.map(para => {
				const trimmed = para.trim();
				if (!trimmed) return '';
				
				// Dentro de cada párrafo, convertir saltos simples a <br>
				const lines = trimmed.split('\n').filter(line => line.trim());
				if (lines.length > 1) {
					return `<p>${lines.join('<br>')}</p>`;
				} else {
					return `<p>${trimmed}</p>`;
				}
			})
			.filter(p => p)
			.join('\n');
	} else {
		// Solo un párrafo o sin párrafos claros
		const trimmed = formatted.trim();
		if (trimmed) {
			// Convertir saltos de línea simples a <br>
			const lines = trimmed.split('\n').filter(line => line.trim());
			if (lines.length > 1) {
				formatted = `<p>${lines.join('<br>')}</p>`;
			} else {
				formatted = `<p>${trimmed}</p>`;
			}
		}
	}
	
	return formatted;
}

function BlogDetail() {
	const { slug } = useParams();
	const navigate = useNavigate();

	const { data: post, isLoading, error } = useQuery({
		queryKey: ['blogPost', slug],
		queryFn: () => contentApi.getBlogPost(slug),
		staleTime: 5 * 60 * 1000,
	});

	// Obtener artículos relacionados
	const { data: relatedPosts = [] } = useQuery({
		queryKey: ['relatedPosts', slug],
		queryFn: () => contentApi.getRelatedBlogPosts(slug),
		staleTime: 5 * 60 * 1000,
		enabled: !!post && post.published,
	});

	// Incrementar vistas al cargar el post
	useEffect(() => {
		if (post && post.published) {
			contentApi.incrementBlogViews(slug).catch(console.error);
		}
	}, [post, slug]);

	// Función para compartir en redes sociales
	const shareOnSocial = (platform) => {
		const url = window.location.href;
		const title = post?.title || '';
		const text = post?.excerpt || '';
		
		let shareUrl = '';
		switch(platform) {
			case 'facebook':
				shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
				break;
			case 'twitter':
				shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`;
				break;
			case 'linkedin':
				shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
				break;
			case 'whatsapp':
				shareUrl = `https://wa.me/?text=${encodeURIComponent(`${title} ${url}`)}`;
				break;
			case 'email':
				shareUrl = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`${text}\n\n${url}`)}`;
				break;
			default:
				return;
		}
		
		if (shareUrl) {
			window.open(shareUrl, '_blank', 'width=600,height=400');
		}
	};

	const formatDate = (dateString) => {
		if (!dateString) return '';
		const date = new Date(dateString);
		return date.toLocaleDateString('es-DO', { year: 'numeric', month: 'long', day: 'numeric' });
	};

	if (isLoading) {
		return (
			<div className="container-width py-12">
				<div className="max-w-4xl mx-auto animate-pulse">
					<div className="h-12 bg-slate-200 rounded w-3/4 mb-4"></div>
					<div className="h-6 bg-slate-200 rounded w-1/2 mb-8"></div>
					<div className="h-64 bg-slate-200 rounded mb-8"></div>
					<div className="space-y-4">
						<div className="h-4 bg-slate-200 rounded"></div>
						<div className="h-4 bg-slate-200 rounded"></div>
						<div className="h-4 bg-slate-200 rounded w-5/6"></div>
					</div>
				</div>
			</div>
		);
	}

	if (error || !post || !post.published) {
		return (
			<div className="container-width py-12">
				<div className="max-w-4xl mx-auto text-center">
					<h1 className="text-3xl font-bold text-primary mb-4">Artículo no encontrado</h1>
					<p className="text-slate-600 mb-6">El artículo que buscas no existe o no está disponible.</p>
					<button
						onClick={() => navigate('/blog')}
						className="px-6 py-3 bg-primary text-white rounded-md hover:bg-primary/90"
					>
						Volver al Blog
					</button>
				</div>
			</div>
		);
	}

	return (
		<div className="container-width py-12">
			<div className="max-w-4xl mx-auto">
				{/* Botón de regreso */}
				<button
					onClick={() => navigate('/blog')}
					className="mb-6 text-primary hover:text-primary/80 flex items-center gap-2"
				>
					<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
					</svg>
					Volver al Blog
				</button>

				{/* Encabezado */}
				<div className="mb-8">
					<div className="flex items-center gap-3 mb-4">
						<span className="text-sm px-3 py-1 bg-primary/10 text-primary rounded-full">
							{post.category_display}
						</span>
						{post.published_at && (
							<span className="text-sm text-slate-600">{formatDate(post.published_at)}</span>
						)}
						{post.views > 0 && (
							<span className="text-sm text-slate-500">{post.views} vistas</span>
						)}
					</div>
					<h1 className="text-4xl sm:text-5xl font-bold text-primary mb-4">{post.title}</h1>
					<div className="flex items-center gap-4 text-slate-600">
						<span>Por {post.author}</span>
					</div>
				</div>

				{/* Imagen destacada */}
				{post.featured_image_url && (
					<img
						src={post.featured_image_url}
						alt={post.title}
						className="w-full h-64 sm:h-96 object-cover rounded-lg mb-8"
					/>
				)}

				{/* Contenido */}
				<div
					className="prose prose-lg max-w-none mb-8"
					dangerouslySetInnerHTML={{ __html: formatBlogContent(post.content) }}
					style={{
						color: '#334155',
						lineHeight: '1.75',
					}}
				/>

				{/* Tags y Compartir */}
				<div className="mb-8 pb-8 border-b">
					{post.tags_list && post.tags_list.length > 0 && (
						<div className="flex flex-wrap gap-2 mb-6">
							{post.tags_list.map((tag, idx) => (
								<span
									key={idx}
									className="text-sm px-3 py-1 bg-slate-100 text-slate-700 rounded-full hover:bg-primary/10 hover:text-primary cursor-pointer transition-colors"
								>
									#{tag}
								</span>
							))}
						</div>
					)}
					
					{/* Botones de compartir */}
					<div className="flex items-center gap-2 flex-wrap">
						<span className="text-sm font-semibold text-slate-700 mr-2">Compartir:</span>
						<button
							onClick={() => shareOnSocial('facebook')}
							className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors"
							aria-label="Compartir en Facebook"
							title="Compartir en Facebook"
						>
							<FacebookIcon className="w-5 h-5" />
						</button>
						<button
							onClick={() => shareOnSocial('twitter')}
							className="p-2 bg-slate-900 text-white rounded-full hover:bg-slate-800 transition-colors"
							aria-label="Compartir en Twitter/X"
							title="Compartir en Twitter/X"
						>
							<XIcon className="w-5 h-5" />
						</button>
						<button
							onClick={() => shareOnSocial('linkedin')}
							className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors"
							aria-label="Compartir en LinkedIn"
							title="Compartir en LinkedIn"
						>
							<LinkedinIcon className="w-5 h-5" />
						</button>
						<button
							onClick={() => shareOnSocial('whatsapp')}
							className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
							aria-label="Compartir en WhatsApp"
							title="Compartir en WhatsApp"
						>
							<svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
								<path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
							</svg>
						</button>
						<button
							onClick={() => shareOnSocial('email')}
							className="p-2 bg-slate-600 text-white rounded-full hover:bg-slate-700 transition-colors"
							aria-label="Compartir por email"
							title="Compartir por email"
						>
							<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
							</svg>
						</button>
					</div>
				</div>

				{/* Artículos relacionados */}
				{relatedPosts && relatedPosts.length > 0 && (
					<div className="mb-8">
						<h2 className="text-2xl font-bold text-primary mb-6">Artículos relacionados</h2>
						<div className="grid md:grid-cols-2 gap-6">
							{relatedPosts.map((relatedPost) => (
								<Link
									key={relatedPost.id}
									to={`/blog/${relatedPost.slug}`}
									className="card overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
								>
									{relatedPost.featured_image_url && (
										<img
											src={relatedPost.featured_image_url}
											alt={relatedPost.title}
											className="w-full h-40 object-cover"
										/>
									)}
									<div className="p-5">
										<div className="flex items-center gap-2 mb-2">
											<span className="text-xs px-2 py-1 bg-primary/10 text-primary rounded-full">
												{relatedPost.category_display}
											</span>
										</div>
										<h3 className="font-bold text-lg text-primary mb-2 line-clamp-2">{relatedPost.title}</h3>
										<p className="text-slate-600 text-sm line-clamp-2">{relatedPost.excerpt}</p>
									</div>
								</Link>
							))}
						</div>
					</div>
				)}

				{/* CTA */}
				<div className="bg-gradient-to-br from-primary to-secondary text-white p-8 rounded-lg text-center">
					<h3 className="text-2xl font-bold mb-4">¿Necesitas asesoría personalizada?</h3>
					<p className="mb-6">Contacta con nuestros expertos y recibe la ayuda que necesitas.</p>
					<div className="flex gap-4 justify-center flex-wrap">
						<a
							href={`https://wa.me/${WHATSAPP_NUMBER}`}
          target="_blank"
							rel="noreferrer"
							className="px-6 py-3 bg-white text-primary rounded-md font-semibold hover:bg-slate-100"
						>
							WhatsApp
						</a>
						<a
							href="#contacto"
							className="px-6 py-3 bg-white/20 text-white rounded-md font-semibold hover:bg-white/30 border border-white"
						>
							Contáctanos
						</a>
					</div>
				</div>
			</div>
    </div>
  );
}

function App() {
	const [selectedService, setSelectedService] = useState(null);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [selectedTeamMember, setSelectedTeamMember] = useState(null);
	const [isTeamModalOpen, setIsTeamModalOpen] = useState(false);

	const openModal = (service) => {
		setSelectedService(service);
		setIsModalOpen(true);
	};

	const closeModal = () => {
		setIsModalOpen(false);
		setSelectedService(null);
	};

	const openTeamModal = (member) => {
		setSelectedTeamMember(member);
		setIsTeamModalOpen(true);
	};

	const closeTeamModal = () => {
		setIsTeamModalOpen(false);
		setSelectedTeamMember(null);
	};

  return (
		<QueryClientProvider client={queryClient}>
			<AuthProvider>
				<BrowserRouter>
					<AppContent 
						openModal={openModal} 
						openTeamModal={openTeamModal}
						selectedService={selectedService}
						isModalOpen={isModalOpen}
						closeModal={closeModal}
						selectedTeamMember={selectedTeamMember}
						isTeamModalOpen={isTeamModalOpen}
						closeTeamModal={closeTeamModal}
					/>
				</BrowserRouter>
			</AuthProvider>
		</QueryClientProvider>
  );
}

function AppContent({ 
	openModal, 
	openTeamModal, 
	selectedService, 
	isModalOpen, 
	closeModal, 
	selectedTeamMember, 
	isTeamModalOpen, 
	closeTeamModal 
}) {
	const location = useLocation();
	
	// Ocultar botones flotantes en las rutas de DGII y administrativas
	const showFloatingButtons = !location.pathname.startsWith('/dgii') && !location.pathname.startsWith('/admin');
	// Ocultar Navbar en rutas administrativas y login
	const showNavbar = !location.pathname.startsWith('/admin') && location.pathname !== '/login';

	return (
		<>
			{showNavbar && <Navbar onServiceClick={openModal} />}
			<Routes>
				<Route path="/" element={<HomePage openModal={openModal} openTeamModal={openTeamModal} />} />
				<Route path="/blog" element={<BlogList />} />
				<Route path="/blog/:slug" element={<BlogDetail />} />
				<Route path="/privacidad" element={<Privacidad />} />
				<Route path="/terminos" element={<Terminos />} />
				<Route path="/cookies" element={<Cookies />} />
				<Route path="/referidores" element={<ReferidoresPage />} />
				<Route path="/empresas-en-carpeta" element={<EmpresasEnCarpeta />} />
				<Route path="/vender-empresa" element={<VenderEmpresa />} />
				<Route path="/documental/cliente/:token" element={<ClienteCambiosDocumental />} />
				<Route path="/login" element={<Login />} />
			<Route path="/register" element={
				<ProtectedRoute requireAdminOrContador={true}>
					<Register />
				</ProtectedRoute>
				} />
				
				{/* Rutas administrativas con layout propio */}
				<Route path="/admin" element={
					<ProtectedRoute>
						<AdminLayout />
					</ProtectedRoute>
				}>
						<Route path="dashboard" element={<AdminDashboard />} />
					<Route path="actividades-economicas" element={
						<ProtectedRoute requireAdmin={true}>
							<GestionActividadesEconomicas />
						</ProtectedRoute>
					} />
				<Route path="empresas" element={<GestionEmpresas />} />
				<Route path="empresas-carpeta" element={
					<ProtectedRoute requireAdmin={true}>
						<EmpresasCarpetaAdmin />
					</ProtectedRoute>
				} />
				<Route path="gestion-empresas" element={
					<ProtectedRoute requireAdmin={true}>
						<GestionEmpresasAdmin />
					</ProtectedRoute>
				} />
					<Route path="documental" element={
						<ProtectedRoute requireAdmin={true}>
							<GestionDocumental />
						</ProtectedRoute>
					} />
					<Route path="forms-fill" element={
						<ProtectedRoute requireAdmin={true}>
							<FormsFillManager />
						</ProtectedRoute>
					} />
					<Route path="dgii" element={<DGIIFormsManager />} />
				<Route path="dgii/admin" element={
					<ProtectedRoute requireGestorOrContador={true}>
						<AdminDGIIView />
					</ProtectedRoute>
				} />
				<Route path="ir-17" element={
					<ProtectedRoute requireGestorOrContador={true}>
						<IR17Manager />
					</ProtectedRoute>
				} />
					<Route path="banners" element={
						<ProtectedRoute requireAdmin={true}>
							<HeroManagement />
						</ProtectedRoute>
					} />
					<Route path="testimonials" element={
						<ProtectedRoute requireAdmin={true}>
							<TestimonialManagement />
						</ProtectedRoute>
					} />
					<Route path="partners" element={
						<ProtectedRoute requireAdmin={true}>
							<PartnerManagement />
						</ProtectedRoute>
					} />
					<Route path="statistics" element={
						<ProtectedRoute requireAdmin={true}>
							<ProjectStatisticManagement />
						</ProtectedRoute>
					} />
					<Route path="blog" element={
						<ProtectedRoute requireAdmin={true}>
							<BlogManagement />
						</ProtectedRoute>
					} />
				<Route path="users" element={
					<ProtectedRoute requireAdminOrContador={true}>
						<UserManagement />
					</ProtectedRoute>
				} />
					<Route path="settings" element={
						<ProtectedRoute requireAdmin={true}>
							<SystemSettings />
						</ProtectedRoute>
					} />
					<Route path="audit" element={
						<ProtectedRoute requireAdmin={true}>
							<AuditLog />
						</ProtectedRoute>
					} />
				</Route>

				{/* Mantener rutas antiguas para compatibilidad (redirigir a /admin) */}
				<Route path="/dgii" element={
					<ProtectedRoute>
						<Navigate to="/admin/dgii" replace />
					</ProtectedRoute>
				} />
			<Route path="/dgii/admin" element={
				<ProtectedRoute requireGestorOrContador={true}>
					<Navigate to="/admin/dgii/admin" replace />
				</ProtectedRoute>
			} />
			</Routes>
			<ServiceModal isOpen={isModalOpen} onClose={closeModal} service={selectedService} />
			<TeamMemberModal isOpen={isTeamModalOpen} onClose={closeTeamModal} member={selectedTeamMember} />
			{showFloatingButtons && <FloatingSocialButton />}
		</>
	);
}

export default App;